// Typed API client for the admin-svc BFF (the staff Admin section).
//
// Base URL: import.meta.env.VITE_ADMIN_BASE_URL (via config; empty ⇒ same-origin
// dev proxy forwards /v1/admin). Auth: the staff JWT from adminAuth, sent as
// `Authorization: Bearer <jwt>`. A 401 throws ApiAuthError (the Admin gate
// bounces to login); a 403 throws ApiForbiddenError (the page shows a no-access
// state, session untouched); other non-2xx throw ApiError.

import { config } from './config';
import { getAdminToken, type AdminProfile, type AdminPermission } from './adminAuth';
import { ApiError, ApiAuthError, ApiForbiddenError } from './api';

// ─── response shapes (mirror the Go JSON) ────────────────────

export interface Person {
  id: number;
  scope: 'customer' | 'driver';
  phone: string;
  country_code: string;
  status: string;
  first_name: string;
  last_name: string;
  email: string;
  profile_picture: string;
  stripe_id: string;
  created_at: string;
  /** Driver service-line classification (the "type" column): premier | logistics
   *  | retail | reserve. '' for customers and unclassified drivers. */
  service_line?: string;
}

/** The driver service lines (product lines, mirroring the trip-type categories). */
export type ServiceLine = 'premier' | 'logistics' | 'retail' | 'reserve';
export const SERVICE_LINES: ServiceLine[] = ['premier', 'logistics', 'retail', 'reserve'];

export interface WalletBalance {
  balance_km: number;
  exists: boolean;
  updated_at: string | null;
}

export interface PeopleResponse {
  status: number;
  scope: string;
  total: number;
  limit: number;
  offset: number;
  items: Person[];
}

export interface Vehicle {
  id: number;
  vehicle_type: number | null;
  brand: string;
  vehicle_name: string;
  color: string;
  vehicle_number: string;
  vehicle_year: number | null;
  status: number;
}

export interface AdminDocument {
  id: number;
  category: string; // driver | vehicle | accreditation
  document_key: string;
  path: string | null;
  status: number; // 14/15/16/17
  status_name: string;
  optional: boolean;
  rejection_reason: string | null;
}

export interface CustomerDetail {
  status: number;
  customer: Person;
  wallet: WalletBalance;
}

export interface DriverDetail {
  status: number;
  driver: Person;
  vehicle: Vehicle | null;
  documents: AdminDocument[];
  wallet: WalletBalance;
}

export interface Trip {
  id: number;
  trip_id: string;
  customer_id: number;
  driver_id: number | null;
  status: number;
  trip_type: number;
  pickup_address: string;
  drop_address: string;
  total: number;
  created_at: string;
}

export interface TripsResponse {
  status: number;
  available: boolean;
  total: number;
  limit: number;
  offset: number;
  items: Trip[];
  note?: string;
}

// ── reserve trips (scheduled, trip_type=14) — the admin "Reserve Trips" list ──
/** One reserve row: the thin trips subset the list renders PLUS the two
 *  reserve-specific schedule fields. `driver_id` is null ("Unassigned") until
 *  the scheduler releases the reserve and a driver accepts. `released` is
 *  server-derived from `reserve_released_at`. */
export interface ReserveTrip {
  id: number;
  trip_id: string;
  customer_id: number;
  driver_id: number | null;
  /** Driver an admin OFFERED this reserve to, awaiting their accept (migration
   *  054). null = not admin-assigned. Set → driver_id null means "pending". */
  assigned_driver_id: number | null;
  status: number;
  pickup_address: string;
  drop_address: string;
  total: number;
  scheduled_pickup_at: string | null; // rider's requested future pickup instant
  reserve_released_at: string | null;  // when the scheduler pushed it into dispatch
  released: boolean;                    // derived: reserve_released_at != null
  /** Telride Boats (trip_type=15) passenger count; 0 for Reserve. */
  number_of_people: number;
  created_at: string;
}

/** KPI roll-up shown in the StatTiles above the table. */
export interface ReserveStats {
  total: number;
  upcoming: number; // held: reserve_released_at IS NULL
  released: number; // already pushed into dispatch
}

export interface ReservesResponse {
  status: number;
  available: boolean; // false when the trips table isn't in this Postgres yet
  total: number;
  limit: number;
  offset: number;
  items: ReserveTrip[];
  stats: ReserveStats;
  note?: string;
}

/** A boat captain's vessel (Telride Boats). image_path/charter_cert_path are
 *  uploads-svc file_url pointers; view them via signDocument like driver docs. */
export interface Vessel {
  id: number;
  user_id: number;
  name: string;
  registration: string;
  capacity: number;
  image_path: string | null;
  charter_cert_path: string | null;
  insurance_expiry: string | null;
  status: number;
}

// ─── trip detail drawer (full row + lifecycle) ───────────────

/** The full single-trip row the detail drawer renders — a superset of Trip. */
export interface TripDetailRow {
  id: number;
  trip_id: string;
  customer_id: number;
  driver_id: number | null;
  status: number;
  trip_type: number;
  trip_sub_type: number;
  booking_type: number;
  vehicle_type: number;
  payment_method: string;
  pickup_address: string;
  pickup_lat: number | null;
  pickup_lng: number | null;
  drop_address: string;
  drop_lat: number | null;
  drop_lng: number | null;
  distance_km: number;
  total: number;
  estimated_fare: number;
  sub_total: number;
  tax: number;
  discount: number;
  tip: number;
  surge: number;
  otp: string;
  // Delivery (Logistics/Retail) + verification chain (migration 064). All ''
  // on ordinary rides / legacy deliveries. The image URLs are PRIVATE-bucket
  // file_urls — view them via signTripMedia (presigned GET), never directly.
  recipient_phone: string;
  recipient_name: string;
  delivery_content: string;
  drop_otp: string;
  signature_url: string;
  sealed_package_url: string;
  delivered_photo_url: string;
  // Retail on-time bonus (mig 065) + customer no-show compensation (mig 066).
  retail_window: string;
  on_time_bonus: number;
  no_show: boolean;
  no_show_fee: number;
  cancel_fee: number;
  pickup_date: string | null;
  start_time: string | null;
  end_time: string | null;
  created_at: string;
  updated_at: string;
}

/** One entry in the lifecycle timeline (from trip_outbox). */
export interface TripLifecycleEvent {
  event_type: string;
  label: string;
  status?: number;
  status_name?: string;
  occurred_at: string;
  published: boolean;
  published_at: string | null;
}

/** Compact participant identity for the drawer header. */
export interface TripParticipant {
  id: number;
  scope: 'customer' | 'driver';
  first_name: string;
  last_name: string;
  phone: string;
  country_code: string;
  status: string;
}

export interface TripDetailResponse {
  status: number;
  available: boolean;
  note?: string;
  trip?: TripDetailRow;
  status_name?: string;
  trip_type?: number;
  trip_type_name?: string;
  customer?: TripParticipant | null;
  driver?: TripParticipant | null;
  lifecycle?: { available: boolean; items: TripLifecycleEvent[]; note?: string };
  complaints?: { available: boolean; items: Complaint[]; note?: string };
  /** Whether the force-cancel control should render enabled (trip in flight). */
  cancellable?: boolean;
}

export interface CancelTripResponse {
  status: number;
  message: string;
  reason?: string;
}

export interface LedgerEntry {
  id: number;
  type: string;
  amount_km: number;
  dollars: number;
  ref: string;
  created_at: string;
}

export interface WalletResponse {
  status: number;
  scope: 'customer' | 'driver';
  user_id: number;
  wallet: WalletBalance;
  ledger: LedgerEntry[];
  buying_rate_per_km: number;
}

export interface FaresResponse {
  status: number;
  editable: boolean;
  buying_rate_per_km: number;
  model: Record<string, unknown>;
  trip_type_rate_fields: string[];
  source: string;
}

// ─── payment history (Stripe top-ups + refund/dispute clawbacks) ──

/** One Stripe-originated money movement, projected from wallet-svc's
 *  wallet_ledger (joined to the rider's auth_users identity). kind is the
 *  primary classifier: 'topup' (money in), 'refund'/'dispute' (money out).
 *  dollars is always a POSITIVE magnitude — direction is carried by kind. */
export interface Payment {
  ledger_id: number;
  kind: 'topup' | 'refund' | 'dispute';
  kind_name: string;
  type: string; // raw wallet_ledger.type (topup | adjustment)
  dollars: number; // AUD, positive magnitude
  amount_km: number; // signed km delta the ledger applied
  reference: string; // Stripe id (PaymentIntent pi_… / charge id, prefix stripped)
  ref: string; // raw ledger ref (idempotency key) — the row key + detail lookup id
  customer_id: number;
  customer_name: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  created_at: string;
}

/** All-time Stripe totals shown above the table. net = topups − refunds − disputes. */
export interface PaymentStats {
  topup_count: number;
  topup_dollars: number;
  refund_count: number;
  refund_dollars: number;
  dispute_count: number;
  dispute_dollars: number;
  net_dollars: number;
}

export interface PaymentsResponse {
  status: number;
  available: boolean;
  total: number;
  limit: number;
  offset: number;
  items: Payment[];
  stats?: PaymentStats;
  note?: string;
}

export interface PaymentDetailResponse {
  status: number;
  available: boolean;
  payment?: Payment;
  note?: string;
}

// ─── driver tips (rider → driver, wallet → wallet) ───────────

/** One tip a rider sent a driver after a trip, projected from earnings-svc's
 *  driver_tips ledger joined to the trip's human code + both parties' identity.
 *  amount is the AUD value (always positive — a tip is a credit). */
export interface DriverTip {
  id: number;
  trip_id: number;
  trip_code: string; // human trips.trip_id ('' if the trip row isn't in this DB)
  trip_label: string; // trip_code, or '#<trip_id>' fallback — the display handle
  customer_id: number;
  customer_name: string;
  customer_first_name: string;
  customer_last_name: string;
  customer_phone: string;
  driver_id: number;
  driver_name: string;
  driver_first_name: string;
  driver_last_name: string;
  driver_phone: string;
  amount: number; // AUD, positive
  idempotency_key: string; // ='tip:<trip_id>' — the ledger's dedupe key
  created_at: string;
}

/** All-time tip totals shown above the table. */
export interface DriverTipStats {
  count: number;
  total_amount: number;
  avg_amount: number;
}

export interface DriverTipsResponse {
  status: number;
  available: boolean;
  total: number;
  limit: number;
  offset: number;
  items: DriverTip[];
  stats?: DriverTipStats;
  note?: string;
}

export interface DriverTipDetailResponse {
  status: number;
  available: boolean;
  tip?: DriverTip;
  note?: string;
}

// ─── driver payouts (Azupay earnings cash-outs) ──────────────

/** The Azupay payout lifecycle statuses (mirrors wallet-svc's driver_payouts
 *  status column). PENDING/CREATED are in-flight; the rest are final. */
export type PayoutStatus = 'PENDING' | 'CREATED' | 'SETTLED' | 'FAILED' | 'RETURNED';

/** One driver cash-out, projected from wallet-svc's driver_payouts (joined to
 *  the driver's auth_users identity). amount is the gross debited; net_amount is
 *  what Azupay transferred to the bank (amount − platform_fee). */
export interface Payout {
  id: number;
  driver_id: number;
  driver_name: string;
  amount: number; // AUD gross debited from the driver's earnings balance
  platform_fee: number; // flat platform fee retained
  net_amount: number; // AUD transferred to the bank (amount − fee)
  payment_method: string; // bsb | payid
  status: PayoutStatus;
  status_name: string;
  azupay_payment_id: string;
  failure_reason: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  settled_at: string | null;
  created_at: string;
}

/** All-time payout totals shown above the table. */
export interface PayoutStats {
  pending_count: number; // PENDING + CREATED (in-flight)
  settled_count: number;
  settled_dollars: number; // gross settled out to drivers
  failed_count: number; // FAILED + RETURNED (refunded)
  fees_dollars: number; // platform fees retained on settled payouts
}

export interface PayoutsResponse {
  status: number;
  available: boolean;
  total: number;
  limit: number;
  offset: number;
  items: Payout[];
  stats?: PayoutStats;
  note?: string;
}

// ─── driver expenses (reimbursement reports) ─────────────────
// Drivers file expenses from the app (title + reason + cost in AUD + an S3
// receipt). earnings-svc owns driver_expenses; admin-svc reads it directly and
// flips the status. status: 0 Pending / 1 Approved / 2 Rejected.

/** One driver expense report row (admin projection + LEFT-JOINed driver name). */
export interface DriverExpense {
  id: number;
  driver_id: number;
  driver_name: string;
  driver_phone?: string | null;
  driver_email?: string;
  title: string;
  reason: string;
  cost: number; // AUD dollars (NOT km-credit)
  evidence_url: string;
  status: 0 | 1 | 2;
  status_name: string;
  created_at: string;
}

/** GET /v1/admin/driver-expenses list response. available=false (+ note) when
 *  the driver_expenses table isn't in this Postgres yet. */
export interface DriverExpensesResponse {
  status: number;
  available: boolean;
  total: number;
  limit: number;
  offset: number;
  items: DriverExpense[];
  note?: string;
}

export interface DriverExpenseDetailResponse {
  status: number;
  available: boolean;
  expense?: DriverExpense;
  note?: string;
}

export interface UpdateExpenseStatusResponse {
  status: number;
  message?: string;
  expense?: DriverExpense;
}

// ─── driver earnings (per-trip earnings ledger) ──────────────

/** One per-trip earning line, projected from earnings-svc's
 *  driver_trip_earnings (joined to the driver's auth_users identity). `amount`
 *  is the driver's total take for the trip (fare + tip) — the live equivalent
 *  of the legacy `driver_earnings.amount` column. */
export interface DriverEarning {
  trip_id: number;
  driver_id: number;
  driver_name: string;
  fare: number; // the driver's cut of the fare (AUD)
  tip: number; // rider tip (AUD; 100% pass-through)
  amount: number; // total take = fare + tip
  distance_km: number;
  pickup_suburb: string;
  drop_suburb: string;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  completed_at: string;
}

/** Earnings totals shown above the table (scoped to the active driver filter). */
export interface DriverEarningsStats {
  trip_count: number;
  fares_dollars: number; // sum of the driver fare cut
  tips_dollars: number; // sum of tips
  total_dollars: number; // fares + tips
}

export interface DriverEarningsResponse {
  status: number;
  available: boolean;
  total: number;
  limit: number;
  offset: number;
  items: DriverEarning[];
  stats?: DriverEarningsStats;
  note?: string;
}

/** Response from the payout-KYC verify write. On success it carries the resulting
 *  verification state + the refreshed record; on a defensive 503/404 it carries a
 *  precise `reason` the UI surfaces verbatim. `record` is omitted here because the
 *  page already refetches the list/queue after a write; `payout_kyc` is the raw
 *  refreshed row the server returns. */
export interface PayoutKYCVerifyResponse {
  status: number;
  message: string;
  reason?: string;
  verification_status?: 'pending' | 'verified';
  verified?: boolean;
  payout_kyc?: PayoutKYCRecord;
}

export interface LoginResponse {
  status: number;
  access_token: string;
  token_type: string;
  expires_in: number;
  admin: AdminProfile;
  permissions: AdminPermission[];
}

export interface MeResponse {
  status: number;
  admin: AdminProfile & { status: string };
  permissions: AdminPermission[];
}

// ─── dashboard summary (platform-stats roll-up for the landing cards) ──

/** The flat platform-stats snapshot the Dashboard landing page binds its cards
 *  to (GET /v1/admin/dashboard/summary). EVERY figure FAILS SOFT server-side: a
 *  failing sub-query or an absent table (pre-cutover) yields 0 for THAT figure
 *  while the rest of the card still renders, so the endpoint always returns 200.
 *
 *  Sources (all read from the shared Postgres the other admin reads use):
 *   - total/approved/pending_drivers — auth_users (scope='driver') +
 *     driver_documents (id_proof approved = approved; total − approved = pending)
 *   - online_drivers   — Redis (drivers:approved ∩ online set); NOT reachable
 *                        from admin-svc yet, so 0 for now
 *   - total_customers  — auth_users (scope='customer')
 *   - trips_today / trips_total — trips (defensive; 0 pre-cutover)
 *   - revenue_today_aud — sum of completed trips' fare today (AUD; 0 pre-cutover)
 *   - wallet_customer_km — total customer km-credit float (wallet_balances)
 *   - wallet_driver_km   — total driver AUD-earnings float (wallet_balances; the
 *                          column is km for customers, dollars for drivers)
 *   - open_complaints      — complaints with status 0 (new/open)
 *   - open_support_tickets — support_threads with status 'open'
 *   - last_updated — RFC3339 server time the snapshot was assembled. */
export interface DashboardSummary {
  status: number;
  total_drivers: number;
  approved_drivers: number;
  pending_drivers: number;
  online_drivers: number;
  total_customers: number;
  trips_today: number;
  trips_total: number;
  revenue_today_aud: number;
  wallet_customer_km: number;
  wallet_driver_km: number;
  open_complaints: number;
  open_support_tickets: number;
  last_updated: string;
}

// ─── complaints / support / incentives + loyalty (read views) ──

export interface Complaint {
  id: number;
  trip_id: number;
  customer_id: number;
  driver_id: number | null;
  complaint_category: number;
  complaint_sub_category: number;
  subject: string;
  description: string;
  status: number;
  created_at: string;
}

export interface ComplaintsResponse {
  status: number;
  available: boolean;
  total: number;
  limit: number;
  offset: number;
  items: Complaint[];
  note?: string;
}

/** Response from the complaint status-update write (admin-svc → comms-svc).
 *  Carries the before/after status so the UI can confirm the transition (and
 *  surface an idempotent no-op when old === new). */
export interface ComplaintStatusResponse {
  status: number;
  message: string;
  id: number;
  old_status: number;
  old_status_name: string;
  new_status: number;
  new_status_name: string;
}

export interface SupportThread {
  customer_id: number;
  status: string;
  assigned_agent_id: number | null;
  last_message_at: string;
  last_preview: string;
  unread_agent: number;
  unread_customer: number;
  created_at: string;
}

export interface SupportThreadsResponse {
  status: number;
  available: boolean;
  total: number;
  limit: number;
  offset: number;
  items: SupportThread[];
  note?: string;
}

export interface SupportMessage {
  id: number;
  sender_role: string;
  sender_id: number;
  agent_name: string;
  body: string;
  attachment_url: string;
  created_at: string;
}

export interface SupportThreadDetail {
  status: number;
  available: boolean;
  customer_id?: number;
  messages?: SupportMessage[];
  note?: string;
}

/** The agent message support-svc inserted (or idempotently replayed) in response
 *  to a reply. Mirrors clients.SupportMessageResult on admin-svc — note
 *  created_at is a string (passed through from support-svc's JSON) and there is
 *  no attachment_url (an agent reply is text). */
export interface SupportReplyMessage {
  id: number;
  customer_id: number;
  sender_role: string;
  sender_id: number;
  agent_name: string;
  body: string;
  created_at: string;
}

/** Response from the agent reply write (admin-svc → support-svc). */
export interface SupportReplyResponse {
  status: number;
  message: string;
  support_message: SupportReplyMessage;
}

/** Response from the claim write — the agent id support-svc recorded as the
 *  thread's assignee (the acting staffer). */
export interface SupportClaimResponse {
  status: number;
  message: string;
  customer_id: number;
  assigned_agent_id: number;
}

/** Response from the close/reopen write — the status support-svc confirmed. */
export interface SupportCloseResponse {
  status: number;
  message: string;
  customer_id: number;
  new_status: string; // 'open' | 'closed'
}

export interface Campaign {
  id: number;
  code: string;
  title: string;
  description: string;
  kind: string;
  target_count: number;
  reward_amount: number;
  trip_type: number | null;
  window_start: string;
  window_end: string;
  active: boolean;
  created_at: string;
}

export interface CampaignsResponse {
  status: number;
  available: boolean;
  total: number;
  limit: number;
  offset: number;
  items: Campaign[];
  note?: string;
}

/** The authorable payload for create/edit (mirrors admin-svc's
 *  campaignAuthorBody → incentives-svc's campaignBody). trip_type is null for
 *  "any trip type"; active defaults to true server-side when omitted. Window
 *  bounds are RFC3339 strings. */
export interface CampaignInput {
  code: string;
  title: string;
  description?: string;
  kind?: string; // 'trip_count' (default) | 'streak'
  target_count: number;
  reward_amount: number;
  trip_type: number | null;
  window_start: string; // RFC3339
  window_end: string; // RFC3339
  active?: boolean;
}

/** Response from the campaign authoring writes (admin-svc → incentives-svc).
 *  Carries the persisted row so the page can reflect the saved campaign without
 *  a refetch. A 503 (ApiError) is surfaced when admin-svc can't reach
 *  incentives-svc yet (secret unset). */
export interface CampaignMutationResponse {
  status: number;
  message: string;
  campaign: Campaign;
}

export interface DriverTier {
  driver_id: number;
  period_start: string;
  points: number;
  trips: number;
  updated_at: string;
  tier: string;
}

export interface DriverTiersResponse {
  status: number;
  available: boolean;
  total: number;
  limit: number;
  offset: number;
  items: DriverTier[];
  note?: string;
}

export interface DriverStreak {
  id: number;
  required_trips: number;
  bonus_amount: number;
  zone_name: string;
  day_of_week: number | null;
  day_name: string;
  time_window_start: string;
  time_window_end: string;
  time_window: string;
  status: number;
  status_name: string;
}

export interface StreaksResponse {
  status: number;
  available: boolean;
  total: number;
  limit: number;
  offset: number;
  items: DriverStreak[];
  note?: string;
}

// ─── driver boosts (incentives-svc owns driver_boosts — READ-ONLY) ──

/** One driver boost — a flat $bonus for completing trips in a named zone during a
 *  time window, optionally constrained to one weekday. incentives-svc OWNS the
 *  `driver_boosts` table; admin-svc only READS it (the same pattern as promos /
 *  payouts). day_of_week: 0=Sun … 6=Sat, or null for "every day". status: 1 =
 *  active (visible to drivers), 0 = inactive. time_window_* are "HH:MM:SS"
 *  wall-clock strings. day_of_week_name is the server-rendered friendly label. */
export interface Boost {
  id: number;
  zone_name: string;
  amount: number;
  time_window_start: string;
  time_window_end: string;
  day_of_week: number | null;
  day_of_week_name: string;
  status: number; // 1 = active, 0 = inactive
}

/** GET /v1/admin/boosts list response. Defensive: available=false (+ note) when
 *  the driver_boosts table isn't in this Postgres yet. writable is always false
 *  today (no admin authoring endpoint exists upstream — see boosts.go). */
export interface BoostsResponse {
  status: number;
  available: boolean;
  writable?: boolean;
  total: number;
  limit: number;
  offset: number;
  items: Boost[];
  note?: string;
}

/** Headline boost totals shown above the table (all display-only glance figures,
 *  computed fail-soft server-side). */
export interface BoostStats {
  total: number;
  active: number;
  max_amount: number; // largest active bonus (0 if none active)
  optins_total: number; // total driver opt-ins across all boosts
}

/** GET /v1/admin/boosts/stats response (same defensive available semantics). */
export interface BoostStatsResponse {
  status: number;
  available: boolean;
  stats?: BoostStats;
  note?: string;
}

// ─── quests (incentives-svc owns driver_quests — READ-ONLY) ──

export interface Quest {
  id: number;
  title: string;
  subtitle: string;
  day_of_week: number | null; // null = every day
  day_label: string; // server-derived ("Monday".."Sunday" / "Every day")
  ends_at: string;
  status: number; // 1 = active, 0 = inactive
  status_name: string; // server-derived ("Active" / "Inactive")
  created_at: string;
}

export interface QuestsResponse {
  status: number;
  available: boolean; // false when driver_quests isn't in this Postgres yet
  total: number;
  limit: number;
  offset: number;
  items: Quest[];
  writable: boolean; // always false today (incentives-svc has no quest authoring)
  writable_note?: string;
  note?: string;
}

export interface QuestDetailResponse {
  status: number;
  available: boolean;
  quest?: Quest;
  note?: string;
}

// ─── quest tiers (incentives-svc owns driver_quest_tiers; incentives.read) ──

/** One quest tier (a `driver_quest_tiers` row), enriched with its parent quest's
 *  title (joined from `driver_quests`). A tier pays a flat `bonus_amount` once a
 *  driver completes `trips_required` qualifying trips on the quest; `tier_order`
 *  is its rung in the ladder. quest_title is '' for an orphaned/untitled quest —
 *  the server also exposes a "Quest #<id>" label via the quests[] options. */
export interface QuestTier {
  id: number;
  quest_id: number;
  quest_title: string;
  tier_order: number;
  trips_required: number;
  bonus_amount: number;
}

/** One (id, title) option for the "filter by quest" dropdown — the distinct
 *  quests that actually have tiers. title is already labelled server-side
 *  ("Quest #<id>" when the quest has no title). */
export interface QuestTierOption {
  id: number;
  title: string;
}

/** GET /v1/admin/incentives/quest-tiers list response. Carries the filter-by-quest
 *  options under `quests` and `read_only: true`. Defensive: available=false (+ note)
 *  when the driver_quest_tiers table isn't in this Postgres yet. */
export interface QuestTiersResponse {
  status: number;
  available: boolean;
  read_only: boolean;
  total: number;
  limit: number;
  offset: number;
  items: QuestTier[];
  quests: QuestTierOption[];
  note?: string;
}

/** GET /v1/admin/incentives/quest-tiers/:id detail response (the read-only drawer). */
export interface QuestTierDetailResponse {
  status: number;
  available: boolean;
  read_only: boolean;
  tier?: QuestTier;
  note?: string;
}

// ─── quest opt-ins (incentives-svc owns driver_quest_optins; incentives.read) ──

export interface QuestOptin {
  id: number;
  driver_id: number;
  driver_name: string;
  driver_label: string; // driver_name, or "#<driver_id>" when the driver row is gone
  quest_id: number;
  quest_title: string;
  quest_label: string; // quest_title, or "#<quest_id>" when the quest row is gone
  trips_completed: number;
  opted_in_at: string;
  created_at: string;
}
export interface QuestOptinFilter {
  id: number;
  title: string;
}
export interface QuestOptinsResponse {
  status: number;
  available: boolean;
  writable?: boolean;
  writable_note?: string;
  total: number;
  limit: number;
  offset: number;
  items: QuestOptin[];
  quest_filters?: QuestOptinFilter[];
  note?: string;
}
export interface QuestOptinStats {
  total: number;
  drivers: number;
  quests: number;
  trips_completed: number;
}
export interface QuestOptinStatsResponse {
  status: number;
  available: boolean;
  stats?: QuestOptinStats;
  note?: string;
}

// ─── guarantee enrollments (incentives-svc owns driver_guarantee_enrollments; incentives.read) ──

/** One `driver_guarantee_enrollments` row, enriched for the staff table. An
 *  earnings guarantee promises a driver at least a guaranteed amount for the
 *  period if they hit the target trips; the platform tops up any shortfall.
 *  driver_label / guarantee_label are the joined display labels, with a "#<id>"
 *  fallback when the parent driver / guarantee row was deleted. earnings_so_far
 *  is AUD. There is no `bonus_paid` field — the Postgres table dropped it. */
export interface GuaranteeEnrollment {
  id: number;
  driver_id: number;
  driver_name: string;
  driver_label: string; // driver_name, or "#<driver_id>" when the driver row is gone
  guarantee_id: number;
  period: string; // the guarantee's period_label, e.g. "this week"
  guarantee_label: string; // period, or "#<guarantee_id>" when the guarantee row is gone
  trips_completed: number;
  earnings_so_far: number; // AUD tracked so far this period
  created_at: string;
  updated_at: string;
}
export interface GuaranteeEnrollmentFilter {
  id: number;
  period: string;
}
export interface GuaranteeEnrollmentsResponse {
  status: number;
  available: boolean; // false when driver_guarantee_enrollments isn't in this Postgres yet
  writable?: boolean; // always false today (incentives-svc has no enrollment authoring)
  writable_note?: string;
  total: number;
  limit: number;
  offset: number;
  items: GuaranteeEnrollment[];
  guarantee_filters?: GuaranteeEnrollmentFilter[];
  note?: string;
}
export interface GuaranteeEnrollmentStats {
  total: number;
  drivers: number;
  guarantees: number;
  earnings_so_far: number;
}
export interface GuaranteeEnrollmentStatsResponse {
  status: number;
  available: boolean;
  stats?: GuaranteeEnrollmentStats;
  note?: string;
}

// ─── driver payout-KYC (driver bank / PayID records — driver-svc owns them) ──

/** How a driver gets paid out, derived server-side from their driver_kyc row:
 *  'payid' (an Azupay PayID is set — the preferred rail) | 'bank' (a BSB is set
 *  — bank transfer) | 'none' (neither — nothing payable submitted yet). */
export type PayoutKYCRail = 'payid' | 'bank' | 'none';

/** One driver bank / payout-KYC record, projected from driver-svc's driver_kyc
 *  (joined to the driver's auth_users identity). user_id IS the driver id
 *  (driver_id is an alias). The dead Nepal-era fields (pan/khalti/esewa) are not
 *  surfaced — the AU payout flow is BSB / PayID only. */
export interface PayoutKYCRecord {
  user_id: number;
  driver_id: number; // alias of user_id
  driver_name: string;
  bank_name: string;
  account_name: string;
  bank_account_number: string;
  bsb: string;
  pay_id: string;
  pay_id_type: string; // email | phone | abn
  rail: PayoutKYCRail;
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  // Verification state (migration 037 — admin-svc owns these governance columns).
  // `verified` is the convenience boolean the badge keys on; verification_status is
  // 'pending' | 'verified'; verified_at / verified_by carry the who/when (null until
  // verified). All optional: an older backend (pre-037) omits them.
  verified?: boolean;
  verification_status?: 'pending' | 'verified';
  verified_at?: string | null;
  verified_by?: number | null;
  updated_at: string;
  created_at: string;
}

/** Headline totals above the table: how many drivers have submitted details and
 *  the breakdown by payout rail vs. still-incomplete. */
export interface PayoutKYCStats {
  total: number; // drivers with a driver_kyc row
  payid_count: number; // a PayID is set
  bank_count: number; // a BSB is set
  incomplete_count: number; // neither PayID nor BSB set
  // migration 037: drivers in the review queue (a payout target AND not verified)
  // and how many are already verified. Optional — a pre-037 backend omits them.
  awaiting_count?: number;
  verified_count?: number;
}

export interface PayoutKYCResponse {
  status: number;
  available: boolean;
  total: number;
  limit: number;
  offset: number;
  items: PayoutKYCRecord[];
  stats?: PayoutKYCStats;
  note?: string;
}

// ─── fare rates (per-category km-credit rate table — admin-svc reads trip-svc's
//     trip_settings + content-svc's trip_types; READ-ONLY this phase) ──

/** The shared time-banded rate block carried by every fare category. An unset
 *  NUMERIC column reads as 0 server-side (COALESCE) and the page renders 0 as
 *  "—". All values are AUD per-km, except driver_rate_subcontractor (flat). */
export interface FareRateBlock {
  customer_rate_day: number;
  customer_rate_evening: number;
  customer_rate_night: number;
  driver_rate_day: number;
  driver_rate_evening: number;
  driver_rate_night: number;
  driver_rate_subcontractor: number;
}

/** One per-category row (one trip type). `rates` is that category's OWN rate
 *  block when the per-category `fare_rates` table is present, else the inherited
 *  global block (rates_inherited=true). status 0 = inactive. */
export interface FareCategory {
  id: number;
  name: string;
  sort: number;
  status: number;
  rates: FareRateBlock;
  /** true ⇒ this category has no per-category row yet, so `rates` is the shared
   *  global block (the page labels it "inherited"). Present once the editable
   *  per-category read (migration 036) is live. */
  rates_inherited?: boolean;
}

/** The editable per-category rate payload (PUT /v1/admin/fares/:trip_type_id/rates).
 *  All 7 fields are required (a category edit sets the whole block, mirroring the
 *  legacy Filament form). AUD/km, except driver_rate_subcontractor (flat). */
export type FareRateInput = FareRateBlock;

/** Response of the per-category rate update. */
export interface FareRateUpdateResponse {
  status: number;
  message?: string;
  fare_rates: FareRateBlock & { trip_type_id: number };
}

/** GET /v1/admin/fares (the richer per-category read, h.FaresRates).
 *  configured=false ⇒ trip_settings is absent/unseeded (the page shows a
 *  "rates not configured" banner; cells read "—"). rates_are_global=true ⇒ every
 *  category shares one rate set. editable is always false this phase;
 *  editable_blocked_reason states exactly why (no dead button). */
export interface FareRatesResponse {
  status: number;
  editable: boolean;
  configured: boolean;
  rates_are_global: boolean;
  buying_rate_per_km: number;
  admin_commission: number;
  model: Record<string, unknown>;
  time_windows: { day: string; evening: string; night: string };
  categories: FareCategory[];
  global_rates: FareRateBlock;
  editable_blocked_reason?: string;
  source?: string;
}

// ─── trip types (service-tier catalogue — content-svc owns the `trip_types`
//     table; admin-svc reads it live + proxies the writes — see handlers/trip_types.go) ──

/** One service-tier row of the catalogue (Premier=1 / Logistics=4 / Retail=13 /
 *  Reserve=14). `id` IS the backend trip_type code the app POSTs (the primary
 *  key — meaningful, not a surrogate). vehicle_mode is the fleet id (18 = Tesla).
 *  status: 1 = active (shown to the apps), 0 = inactive. */
export interface TripType {
  id: number;
  name: string;
  vehicle_mode: number;
  sort: number;
  status: number; // 1 = active, 0 = inactive
  active_icon: string;
  inactive_icon: string;
}

/** The authorable trip-type payload (mirrors admin-svc's tripTypeWriteBody →
 *  content-svc). On create `id` is the trip_type code (required, positive); on
 *  edit it comes from the path and is ignored. */
export interface TripTypeInput {
  id: number;
  name: string;
  vehicle_mode: number;
  sort: number;
  status: number; // 1 = active, 0 = inactive
  active_icon: string;
  inactive_icon: string;
}

/** GET /v1/admin/trip-types list response. Defensive: available=false (+ note)
 *  when the `trip_types` table isn't in this Postgres yet. */
export interface TripTypesResponse {
  status: number;
  available: boolean;
  total: number;
  items: TripType[];
  note?: string;
}

/** Response from the trip-type writes (create/edit/delete/status). content-svc
 *  echoes the persisted row under `trip_type` on create/edit; delete/status fall
 *  back to a plain confirmation. The page only reads success/failure, so the row
 *  is optional. */
export interface TripTypeMutationResponse {
  status: number;
  message?: string;
  trip_type?: TripType;
}

// ─── vehicle categories (content-svc owns vehicle_categories; cms.read; READ-ONLY) ──

export interface VehicleCategory {
  id: number;
  vehicle_type: string;
  description: string;
  base_fare: number;
  price_per_km: number;
  /** Per-km SURCHARGE (migration 044) — the extra per-km the premium Tesla models
   *  add. `customer` is charged to the rider; `driver` is paid straight through to
   *  the driver (platform takes $0 on it). 0 on a pre-044 Postgres. These two are
   *  the ONLY editable fields on the page; base_fare/price_per_km are read-only
   *  (not wired into fare calc). */
  price_per_km_customer_surcharge: number;
  price_per_km_driver_surcharge: number;
  active_icon: string;
  inactive_icon: string;
  status: number; // 1 = Active, 0 = Inactive
}

/** The editable surcharge payload (PUT /v1/admin/vehicle-categories/:id/pricing).
 *  Both fields required, AUD/km, >= 0. */
export interface VehicleCategoryPricingInput {
  price_per_km_customer_surcharge: number;
  price_per_km_driver_surcharge: number;
}

/** Response of the surcharge update — content-svc echoes the persisted row under
 *  `vehicle_category` (id + the two surcharges). */
export interface VehicleCategoryPricingResponse {
  status: number;
  message?: string;
  vehicle_category?: {
    id: number;
    vehicle_type: string;
    price_per_km_customer_surcharge: number;
    price_per_km_driver_surcharge: number;
  };
}

export interface VehicleCategoriesResponse {
  status: number;
  available: boolean;
  total: number;
  limit: number;
  offset: number;
  items: VehicleCategory[];
  note?: string;
}

// ─── complaint categories (content-svc owns the tables; cms.read; READ-ONLY) ──

/** One complaint_sub_categories row — a sub-reason under a parent category.
 *  `complaint_category` is the owning category id. `short_description` is ''
 *  when the column is NULL (server COALESCEs it). */
export interface ComplaintSubCategory {
  id: number;
  complaint_category: number;
  complaint_sub_category_name: string;
  short_description: string;
  status: number; // 1 = Active, 0 = Inactive
}

/** One complaint_categories row — a top-level bucket. `sub_count` is always set
 *  (list view); `sub_categories` is only populated by the by-id detail fetch. */
export interface ComplaintCategory {
  id: number;
  complaint_category_name: string;
  status: number; // 1 = Active, 0 = Inactive
  sub_count: number;
  sub_categories?: ComplaintSubCategory[];
}

export interface ComplaintCategoriesResponse {
  status: number;
  available: boolean;
  total: number;
  limit: number;
  offset: number;
  items: ComplaintCategory[];
  note?: string;
}

/** Authorable parent-category payload (mirrors admin-svc's complaintCategoryBody).
 *  status: 1=Active, 0=Inactive (defaults to Active server-side when omitted). */
export interface ComplaintCategoryInput {
  complaint_category_name: string;
  status: number;
}

/** Authorable sub-category payload (mirrors admin-svc's complaintSubCategoryBody).
 *  The parent id comes from the create route, not the body, so it isn't here. */
export interface ComplaintSubCategoryInput {
  complaint_sub_category_name: string;
  short_description: string;
  status: number;
}

export interface ComplaintCategoryMutationResponse {
  status: number;
  category?: ComplaintCategory;
  message?: string;
}

export interface ComplaintSubCategoryMutationResponse {
  status: number;
  sub_category?: ComplaintSubCategory;
  message?: string;
}

// ─── booking statuses (booking_statuses lookup — admin-svc owns the CRUD
//     directly: a pure config/lookup table with no owning Go service) ──

/** One booking_statuses row — the internal status label + the customer-facing
 *  message. The id IS the status code the apps key on. Mirrors the legacy
 *  Filament resource. */
export interface BookingStatus {
  id: number;
  status_name: string;
  customer_status_name: string;
}

/** The authorable payload for create/edit. status_name + customer_status_name
 *  are both required. */
export interface BookingStatusInput {
  status_name: string;
  customer_status_name: string;
}

/** GET /v1/admin/booking-statuses list response. Defensive: available=false
 *  (+ note) when the booking_statuses table isn't in this Postgres yet. */
export interface BookingStatusesResponse {
  status: number;
  available: boolean;
  items: BookingStatus[];
  note?: string;
}

/** Response from the booking-status writes — echoes the persisted row under
 *  `booking_status` on create/edit; delete returns a plain confirmation. The
 *  page only reads success/failure, so the row is optional. */
export interface BookingStatusMutationResponse {
  status: number;
  message?: string;
  booking_status?: BookingStatus;
}

// ─── promo codes (customer promo codes — incentives-svc owns them) ──

/** A customer promo code (the rider Promo screen reads the active ones). The
 *  admin view lists ALL statuses. promo_for is the target audience: 'ride' (a
 *  trip-fare discount) or 'wallet' (a top-up bonus). status: 1 = active,
 *  0 = inactive. discount/min_fare/max_discount_value are AUD amounts. */
export interface Promo {
  id: number;
  promo_name: string;
  promo_code: string;
  description: string;
  promo_for: string; // 'ride' | 'wallet'
  discount: number;
  min_fare: number;
  max_discount_value: number;
  status: number; // 1 = active, 0 = inactive
  created_at: string;
}

export interface PromosResponse {
  status: number;
  available: boolean;
  total: number;
  limit: number;
  offset: number;
  items: Promo[];
  note?: string;
}

/** The authorable payload for create/edit (mirrors admin-svc's promoAuthorBody →
 *  incentives-svc's promoBody). status defaults to active (1) server-side when
 *  omitted. */
export interface PromoInput {
  name: string;
  code: string;
  description?: string;
  promo_for: string; // 'ride' | 'wallet'
  discount: number;
  min_fare: number;
  max_discount_value: number;
  status?: number; // 1 = active, 0 = inactive
}

/** Response from the promo authoring writes (admin-svc → incentives-svc). Carries
 *  the persisted row so the page can reflect the saved promo without a refetch.
 *  A 503 (ApiError) is surfaced when admin-svc can't reach incentives-svc yet
 *  (secret unset). */
export interface PromoMutationResponse {
  status: number;
  message: string;
  promo: Promo;
}

export interface AuditEntry {
  id: number;
  actor_email: string;
  action: string;
  target_type: string;
  target_id: string;
  result: string;
  created_at: string;
  /** Full detail + request metadata (migration 040). detail is a raw JSON
   *  object string; the rest may be '' on historical rows. */
  detail?: string;
  request_ip?: string;
  user_agent?: string;
  http_method?: string;
  http_route?: string;
}

export interface AuditResponse {
  status: number;
  items: AuditEntry[];
  total?: number;
  limit?: number;
  offset?: number;
}

export interface StaffUser {
  id: number;
  email: string;
  name: string;
  role: string; // superadmin | ops | finance | support
  status: string; // active | disabled
  created_at: string;
  updated_at: string;
}

export interface StaffListResponse {
  status: number;
  total: number;
  limit: number;
  offset: number;
  items: StaffUser[];
}

export interface StaffMutationResponse {
  status: number;
  message: string;
  staff: StaffUser;
}

export interface CreateStaffResponse {
  status: number;
  message: string;
  staff: { id: number; email: string; name: string; role: string };
}

// ─── roles + permissions (DB-backed custom roles, migration 041) ─────────────

/** One row of the Roles list — a role + its counts. is_system roles (the four
 *  built-ins) are immutable; custom roles are editable/deletable. */
export interface RoleSummary {
  name: string;
  description: string;
  is_system: boolean;
  permission_count: number;
  staff_count: number;
}

export interface RolesListResponse {
  status: number;
  available: boolean;
  items: RoleSummary[];
}

/** A role + its effective permission set (the code matrix for system roles, the
 *  stored subset for custom ones). */
export interface RoleDetail {
  name: string;
  description: string;
  is_system: boolean;
  permissions: string[];
}

/** One permission-catalogue entry for the role builder: the permission string +
 *  its category (the segment before the first dot, e.g. "drivers"). */
export interface PermissionInfo {
  permission: string;
  category: string;
}

export interface ResetStaffPasswordResponse {
  status: number;
  message: string;
  staff: { id: number; email: string };
}

export interface ReviewResponse {
  status: number;
  message: string;
  document_key: string;
  new_status: number;
  status_name: string;
}

/** Maker step: queuing an adjustment returns its pending id (no money moved). */
export interface AdjustResponse {
  status: number;
  message: string;
  id: number;
  adjustment_status: 'pending';
}

/** One row of the maker-checker approval queue (wallet_adjustments). */
export interface WalletAdjustment {
  id: number;
  scope: 'customer' | 'driver';
  target_user_id: number;
  direction: 'credit' | 'debit';
  dollars: number;
  note: string;
  status: 'pending' | 'applied' | 'rejected';
  maker_admin_id: number;
  maker_email: string;
  checker_admin_id?: number | null;
  checker_email?: string;
  applied_balance?: number | null;
  last_error?: string;
  created_at: string;
  decided_at?: string | null;
}

export interface WalletAdjustmentsResponse {
  status: number;
  adjustments: WalletAdjustment[];
}

/** Checker step: approve/reject result. */
export interface AdjustDecisionResponse {
  status: number;
  message: string;
  id: number;
  adjustment_status: 'applied' | 'rejected';
  wallet?: number;
}

/** Response from the trust-&-safety block/ban writes. account_status is the new
 *  auth_users.status ('blocked' | 'active') as confirmed by auth-svc. */
export interface BlockBanResponse {
  status: number;
  message: string;
  account_status: string;
}

/** Response from the per-user force-logout write. admin-svc proxies auth-svc
 *  token revocation (every active session dies) WITHOUT changing the account
 *  status — the user can log straight back in. */
export interface ForceLogoutResponse {
  status: number;
  message: string;
}

/** Response of PATCH /v1/admin/customers/:id/identity — the display fields as
 *  persisted by auth-svc (so the drawer can refresh from the source). */
export interface EditCustomerIdentityResponse {
  status: number;
  message: string;
  first_name: string;
  last_name: string;
  email: string;
}

// ─── CMS / content (content-svc owns the faqs / policies / app-settings /
//     contact tables; admin-svc proxies the writes — see handlers/cms.go) ──

/** One help-centre FAQ. user_type_id is the audience (1 = customer, 2 = driver);
 *  status: 1 = published, 0 = draft. */
export interface CMSFaq {
  id: number;
  user_type_id: number;
  question: string;
  answer: string;
  status: number; // 1 = published, 0 = draft
  created_at?: string;
}

/** The authorable FAQ payload (mirrors admin-svc's faqWriteBody → content-svc's
 *  faqBody). status is the numeric flag (1 = published, 0 = draft). */
export interface CMSFaqInput {
  user_type_id: number;
  question: string;
  answer: string;
  status: number; // 1 = published, 0 = draft
}

/** A legal/about policy document. slug is the stable key the app fetches by
 *  (e.g. privacy-policy); user_type_id is the audience (1/2); status: 1/0. */
export interface CMSPolicy {
  id: number;
  slug: string | null;
  title: string;
  description: string;
  user_type_id: number;
  status: number; // 1 = published, 0 = draft
  created_at?: string;
}

/** The authorable policy payload (mirrors admin-svc's policyWriteBody →
 *  content-svc's policyBody). */
export interface CMSPolicyInput {
  slug: string;
  title: string;
  description: string;
  user_type_id: number;
  status: number; // 1 = published, 0 = draft
}

/** The app_settings singleton (global app config content-svc serves to both
 *  apps). The portal loads then re-saves the whole row; admin-svc forwards it to
 *  content-svc which UPSERTs the singleton. */
export interface CMSAppSettings {
  id: number;
  app_name: string;
  logo: string;
  app_version: string;
  driver_app_version: string;
  default_currency: string;
  default_currency_symbol: string;
  currency_short_code: string;
  default_country: string;
  phone_code: string;
  about_us: string;
  subscription_status: number;
  language_status: number;
  default_language: string;
  polyline_status: number;
  driver_trip_time: number;
  capital_lat: number;
  capital_lng: number;
}

/** The contact_settings singleton (the support-contact card the apps show).
 *  lat/lng are strings (passed through from content-svc's legacy row). */
export interface CMSContact {
  id: number;
  phone_number: string;
  email: string;
  address: string;
  lat: string;
  lng: string;
}

/** GET list response for FAQs / policies. Defensive: available=false (+ note)
 *  when the underlying content-svc table isn't in this Postgres yet. */
export interface CMSFaqsResponse {
  status: number;
  available: boolean;
  items: CMSFaq[];
  note?: string;
}

export interface CMSPoliciesResponse {
  status: number;
  available: boolean;
  items: CMSPolicy[];
  note?: string;
}

/** GET response for the app-settings singleton. available=false ⇒ the table is
 *  absent; configured=false ⇒ the table exists but no row is seeded yet (the
 *  form starts empty and a save creates it). */
export interface CMSAppSettingsResponse {
  status: number;
  available: boolean;
  configured?: boolean;
  app_settings?: CMSAppSettings;
  note?: string;
}

/** One published app build for a platform (content-svc's app_versions row;
 *  migration 055). version_code is the BUILD NUMBER the apps compare their
 *  installed build against; force_update=1 HARD-BLOCKS any older build until
 *  the user updates. platform: 1 = Android, 2 = iOS. */
export interface CMSAppVersion {
  id?: number;
  platform: number; // 1 = Android, 2 = iOS
  user_type_id?: number; // 1 = customer, 2 = driver (migration 056)
  version_number: string; // marketing label, e.g. "1.4.0"
  version_code: string; // build number the apps compare against
  force_update: number; // 0 | 1 — 1 hard-blocks older builds
}

/** Read used to SEED the force-update forms. The portal only talks to admin-svc:
 *  GET /v1/admin/cms/app-version returns the currently-published builds for each
 *  platform × app (customer + driver, migration 056) — admin-svc proxies
 *  content-svc's app-settings payload behind cms.manage. Each slot is an
 *  app_versions row or null when none is published yet. Best-effort: the admin
 *  endpoint degrades to null rows so the forms start empty and a save still
 *  publishes a build. */
export interface CMSAppVersionsResponse {
  android: CMSAppVersion | null;
  ios: CMSAppVersion | null;
  android_driver: CMSAppVersion | null;
  ios_driver: CMSAppVersion | null;
}

/** Raw shape of GET /v1/admin/cms/app-version (admin-svc). Each result slot is a
 *  published build row, or null when none/unreachable. */
interface AdminAppVersionsResponse {
  status: number;
  result?: {
    android?: CMSAppVersion | null;
    ios?: CMSAppVersion | null;
    android_driver?: CMSAppVersion | null;
    ios_driver?: CMSAppVersion | null;
  };
}

/** GET response for the contact singleton (same available/configured semantics
 *  as app-settings). */
export interface CMSContactResponse {
  status: number;
  available: boolean;
  configured?: boolean;
  contact?: CMSContact;
  note?: string;
}

// ─── operating zones (content-svc owns the `zones` table; admin-svc proxies
//     the list + the writes through the ContentClient — see handlers/zones.go) ──

/** One service zone — a CIRCLE (content-svc migration 054, `service_zones`):
 *  a named centre + radius_km, with an active flag. "In service" = inside ANY
 *  active zone. The rider/driver apps + the trip-svc/driver-svc gates read these. */
export interface Zone {
  id: number;
  name: string;
  active: boolean;
  center_lat: number;
  center_lng: number;
  radius_km: number;
}

/** The authorable zone payload (mirrors admin-svc's zoneWriteBody →
 *  content-svc's serviceZoneBody). active defaults true server-side when omitted. */
export interface ZoneInput {
  name: string;
  active: boolean;
  center_lat: number;
  center_lng: number;
  radius_km: number;
}

/** GET /v1/admin/zones list response. Defensive: available=false (+ note) when
 *  admin-svc can't reach content-svc's service-zones admin endpoint yet. */
export interface ZonesResponse {
  status: number;
  available: boolean;
  items: Zone[];
  note?: string;
}

/** Response from the zone writes. content-svc echoes the persisted row under
 *  `service_zone` on create/edit; delete falls back to a plain confirmation. */
export interface ZoneMutationResponse {
  status: number;
  message?: string;
  service_zone?: Zone;
}

// ─── Telride Boats docks (admin-svc owns docks/dock_distances directly, like
//     booking-statuses). docks.read for the list, docks.manage for the writes.
//     DARK until launch — pure reference-data authoring; nothing consumes it. ──

/** One named pickup/drop point a Boats charter runs between. */
export interface Dock {
  id: number;
  name: string;
  lat: number;
  lng: number;
  address: string;
  is_active: boolean;
}

/** Authorable dock payload. is_active defaults true server-side when omitted. */
export interface DockInput {
  name: string;
  lat: number;
  lng: number;
  address: string;
  is_active?: boolean;
}

/** GET /v1/admin/docks list response. Defensive: available=false (+ note) until
 *  migration 056 lands. */
export interface DocksResponse {
  status: number;
  available: boolean;
  items: Dock[];
  note?: string;
}

/** The admin-set billable km for an ORDERED (from,to) dock pair. */
export interface DockDistance {
  id: number;
  from_dock_id: number;
  to_dock_id: number;
  distance_km: number;
}

/** GET /v1/admin/dock-distances list response (the per-pair km matrix). */
export interface DockDistancesResponse {
  status: number;
  available: boolean;
  items: DockDistance[];
  note?: string;
}

/** Response from the dock writes. */
export interface DockMutationResponse {
  status: number;
  message?: string;
  dock?: Dock;
  dock_distance?: DockDistance;
}

// ─── notifications (notification-svc owns FCM delivery + the in-app feed;
//     admin-svc proxies the send + sources history from the audit trail —
//     see handlers/notifications.go) ──

/** The audiences the compose form + the send proxy accept. 'all_*' are role-wide
 *  broadcasts; 'user' targets one auth_users id within a scope. */
export type NotificationAudience = 'all_customers' | 'all_drivers' | 'user';

/** One row of the recent-sends history, projected from admin_audit_log (the
 *  authoritative record of staff broadcasts — notification-svc's own feed is
 *  per-end-user). result: 'ok' | 'error'. */
export interface SentNotification {
  id: number;
  actor_email: string;
  title: string;
  preview: string;
  audience: string;
  scope: string;
  user_id: number;
  recipients: number;
  sent: number;
  failed: number;
  result: string; // 'ok' | 'error'
  created_at: string;
}

export interface NotificationHistoryResponse {
  status: number;
  items: SentNotification[];
  limit?: number;
}

/** The compose payload (mirrors admin-svc's sendNotificationBody). A discriminated
 *  union: a 'user' send carries scope + user_id; the broadcasts don't. data is an
 *  optional flat string map forwarded as the FCM data payload (e.g. a deep-link). */
export type SendNotificationInput =
  | { title: string; body: string; audience: 'all_customers' | 'all_drivers'; data?: Record<string, string> }
  | {
      title: string;
      body: string;
      audience: 'user';
      scope: 'customer' | 'driver';
      user_id: number;
      data?: Record<string, string>;
    };

/** Response from the send proxy. recipients/sent/failed are the delivery counts
 *  notification-svc reported (the compose card surfaces them). */
export interface SendNotificationResponse {
  status: number;
  message: string;
  audience: string;
  recipients: number;
  sent: number;
  failed: number;
}

// ─── transport ───────────────────────────────────────────────

function buildUrl(path: string, query?: Record<string, string | number | undefined>): string {
  const base = config.adminBaseUrl;
  const qs = query
    ? Object.entries(query)
        .filter(([, v]) => v !== undefined && v !== '')
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join('&')
    : '';
  const sep = qs ? (path.includes('?') ? '&' : '?') : '';
  return `${base}${path}${sep}${qs}`;
}

/** Map a non-2xx response to the right error class (auth vs generic). */
async function raise(res: Response): Promise<never> {
  // 401 = dead token (session over). 403 = valid session, role not allowed —
  // the caller renders a no-access state; it must NOT end the session.
  if (res.status === 401) throw new ApiAuthError(res.status);
  if (res.status === 403) throw new ApiForbiddenError();
  let detail = `${res.status} ${res.statusText}`;
  try {
    const body = await res.text();
    if (body) {
      // admin-svc returns {status,message,...}; surface message when present.
      try {
        const j = JSON.parse(body) as { message?: string };
        detail = j.message || body;
      } catch {
        detail = body;
      }
    }
  } catch {
    /* ignore body read errors */
  }
  throw new ApiError(res.status, detail);
}

function authHeaders(json = false): HeadersInit {
  const h: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${getAdminToken()}`,
  };
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

async function getJSON<T>(
  path: string,
  query?: Record<string, string | number | undefined>,
  signal?: AbortSignal,
): Promise<T> {
  const res = await fetch(buildUrl(path, query), { method: 'GET', headers: authHeaders(), signal });
  if (!res.ok) return raise(res);
  return (await res.json()) as T;
}

async function postJSON<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(buildUrl(path), {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) return raise(res);
  return (await res.json()) as T;
}

async function patchJSON<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(buildUrl(path), {
    method: 'PATCH',
    headers: authHeaders(true),
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) return raise(res);
  return (await res.json()) as T;
}

async function putJSON<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(buildUrl(path), {
    method: 'PUT',
    headers: authHeaders(true),
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) return raise(res);
  return (await res.json()) as T;
}

async function deleteJSON<T>(path: string): Promise<T> {
  const res = await fetch(buildUrl(path), { method: 'DELETE', headers: authHeaders() });
  if (!res.ok) return raise(res);
  return (await res.json()) as T;
}

// ─── endpoints ───────────────────────────────────────────────

/** One logged TEST-NUMBER OTP code (auth-svc writes these for OTP_TEST_NUMBERS;
 *  a real user's code is never logged). */
export interface TestOtpEntry {
  phone: string;
  code: string;
  created_at: string;
}
export interface OtpCodesResponse {
  status: number;
  items: TestOtpEntry[];
}

/** One FCM push attempt + its result (godview push log). Written by
 *  notification-svc on every send — success, failure, AND no-token. */
export interface PushLogEntry {
  id: number;
  scope: string; // driver | customer
  recipient_id: number;
  kind: string; // data.type (trip_offered/chat/support/…) or '' for a plain push
  token_present: boolean;
  ok: boolean;
  provider_status: number; // FCM HTTP status (0 = never sent)
  provider_response: string; // FCM body / error reason
  error: string;
  created_at: string;
}
export interface PushLogResponse {
  status: number;
  items: PushLogEntry[];
}

/** One driver/applicant selfie face-match attempt + result (godview ID-verification
 *  log). Written by verification-svc (selfie_checks). */
export interface VerificationLogEntry {
  id: number;
  subject_type: string; // driver | applicant
  subject_id: number;
  score: number | null; // similarity 0..1, null on matcher error
  matched: boolean;
  status: string; // matched | no_match
  biometric_consent_at: string | null;
  biometric_consent_version: string;
  created_at: string;
}
export interface VerificationLogResponse {
  status: number;
  items: VerificationLogEntry[];
}

// ─── EV charging stations (admin-curated; drivers find a charger between trips).
//     admin-svc owns the `chargers` table directly, like docks/booking-statuses.
//     cms.read lists, cms.manage writes. ──
export interface Charger {
  id: number;
  name: string;
  address: string;
  lat: number;
  lng: number;
  network: string;   // e.g. "Tesla Supercharger"
  connector: string; // e.g. "Tesla / CCS2"
  stalls: number;
  notes: string;
  active: boolean;
}
export interface ChargerInput {
  name: string;
  address: string;
  lat: number;
  lng: number;
  network: string;
  connector: string;
  stalls: number;
  notes: string;
  active: boolean;
}
export interface ChargersResponse {
  status?: number;
  chargers: Charger[];
}

// ─── Speeding events (proof-in-records when a customer reports a driver
//     was over the posted limit). safety-svc detects + records; admin-svc reads
//     the shared `speeding_events` table. audit/safety read permission. ──
export interface SpeedingEvent {
  id: number;
  trip_id: number;
  driver_id: number;
  occurred_at: string;
  lat: number;
  lng: number;
  observed_kmh: number;
  limit_kmh: number;
  over_kmh: number;
  road: string;
  sustained_s: number;
}
export interface SpeedingResponse {
  status?: number;
  events: SpeedingEvent[];
}

export interface DestinationSettingsResponse {
  status: number;
  available: boolean;
  daily_limit: number;
  enabled: boolean;
  note?: string;
}

export interface FeatureSettingsResponse {
  status: number;
  available: boolean;
  split_fare_enabled: boolean;
  masked_calling_enabled: boolean;
  wait_time_enabled: boolean;
  wait_time_price_per_min: number;
  wait_time_grace_min: number;
  // Surge pricing (migration 063).
  pickup_surge_enabled: boolean;
  pickup_surge_flat_day_rate: boolean;
  short_ride_surge_flat_day_rate: boolean;
  note?: string;
}

// Customer membership tiers — the Silver/Gold/Platinum-Black ladder (km-consumed
// thresholds + top-up discount %), DB-backed + admin-editable. wallet-svc reads
// it live. The GET also returns the live tier distribution (member counts).
export interface MembershipTierDistribution {
  base: number;
  silver: number;
  gold: number;
  platinum: number;
}
export interface MembershipTiersResponse {
  status: number;
  available: boolean;
  enabled: boolean;
  silver_km: number;
  gold_km: number;
  platinum_km: number;
  silver_pct: number;
  gold_pct: number;
  platinum_pct: number;
  distribution?: MembershipTierDistribution;
  note?: string;
}
export interface MembershipTiersInput {
  enabled: boolean;
  silver_km: number;
  gold_km: number;
  platinum_km: number;
  silver_pct: number;
  gold_pct: number;
  platinum_pct: number;
}

export const adminApi = {
  /** POST /v1/admin/login — email+password → staff JWT. Does NOT use the
   *  stored token (there isn't one yet); sends only the credentials. */
  async login(email: string, password: string): Promise<LoginResponse> {
    const res = await fetch(buildUrl('/v1/admin/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) return raise(res);
    return (await res.json()) as LoginResponse;
  },

  customers(params: { q?: string; limit?: number; offset?: number }, signal?: AbortSignal) {
    return getJSON<PeopleResponse>('/v1/admin/customers', params, signal);
  },
  customer(id: number, signal?: AbortSignal) {
    return getJSON<CustomerDetail>(`/v1/admin/customers/${id}`, undefined, signal);
  },
  /** POST /v1/admin/customers/:id/block — block (action:'block') or reinstate
   *  (action:'unblock') a customer via auth-svc set-status (status flip +
   *  token revocation). Gated by customers.block server-side; audited. */
  blockCustomer(id: number, action: 'block' | 'unblock', reason?: string) {
    return postJSON<BlockBanResponse>(`/v1/admin/customers/${id}/block`, { action, reason: reason ?? '' });
  },
  /** POST /v1/admin/customers/:id/force-logout — revoke every active session for
   *  the customer via auth-svc token revocation. Does NOT change account status;
   *  the user can log back in immediately. Gated server-side; audited. */
  forceLogoutCustomer(id: number) {
    return postJSON<ForceLogoutResponse>(`/v1/admin/customers/${id}/force-logout`, {});
  },
  /** PATCH /v1/admin/customers/:id/identity — edit a customer's display
   *  identity (first/last/email) via auth-svc update-identity (auth-svc owns
   *  identity). NEVER touches phone/password/status/stripe_id. Gated by
   *  customers.manage server-side; audited. A 409 means the email is already
   *  used by another customer. */
  editCustomerIdentity(id: number, body: { first_name?: string; last_name?: string; email?: string }) {
    return patchJSON<EditCustomerIdentityResponse>(`/v1/admin/customers/${id}/identity`, {
      first_name: body.first_name ?? '',
      last_name: body.last_name ?? '',
      email: body.email ?? '',
    });
  },
  drivers(params: { q?: string; limit?: number; offset?: number }, signal?: AbortSignal) {
    return getJSON<PeopleResponse>('/v1/admin/drivers', params, signal);
  },
  driver(id: number, signal?: AbortSignal) {
    return getJSON<DriverDetail>(`/v1/admin/drivers/${id}`, undefined, signal);
  },
  /** POST approve/reject a driver document → driver-svc /sync. */
  reviewDocument(driverId: number, documentKey: string, decision: 'approve' | 'reject', reason?: string) {
    return postJSON<ReviewResponse>(`/v1/admin/drivers/${driverId}/documents/review`, {
      document_key: documentKey,
      decision,
      reason: reason ?? '',
    });
  },
  /** POST mint a SHORT-LIVED presigned GET so a reviewer can VIEW a private-bucket
   *  document (the stored path 403s on a direct GET). Returns a temporary URL. */
  signDocument(driverId: number, path: string) {
    return postJSON<{ status: number; url: string; expires_in: number }>(
      `/v1/admin/drivers/${driverId}/documents/sign`,
      { path },
    );
  },
  /** Presign an S3 PUT so staff can upload a driver document ON THE DRIVER'S
   *  BEHALF (admin-svc → uploads-svc, server-to-server). admin-svc derives the S3
   *  `kind` from the document's scope (driver|accreditation → onboarding_document,
   *  vehicle → vehicle_photo). The browser then PUTs the file bytes to upload_url
   *  with the returned signed headers; the resulting file_url is saved via
   *  uploadDriverDocument. scope = driver|vehicle|accreditation. */
  presignDriverDocument(driverId: number, scope: string, contentType: string, ext: string) {
    return postJSON<{
      status: number;
      upload_url: string;
      method: string;
      headers: Record<string, string>;
      file_url: string;
      expires_in: number;
    }>(`/v1/admin/drivers/${driverId}/documents/presign`, {
      scope,
      content_type: contentType,
      ext,
    });
  },
  /** Save a staff-uploaded driver document (after the S3 PUT). Sets the doc's
   *  path + moves it to PENDING review (status 15). scope = driver|vehicle|accreditation. */
  uploadDriverDocument(driverId: number, scope: string, documentKey: string, fileUrl: string) {
    return postJSON<ReviewResponse>(`/v1/admin/drivers/${driverId}/documents`, {
      scope,
      document_key: documentKey,
      file_url: fileUrl,
    });
  },
  /** POST /v1/admin/drivers/:id/ban — ban (action:'ban') or reinstate
   *  (action:'unban') a driver via auth-svc set-status (status flip + token
   *  revocation; a blocked driver also drops out of dispatch). Gated by
   *  drivers.ban server-side; audited. */
  banDriver(id: number, action: 'ban' | 'unban', reason?: string) {
    return postJSON<BlockBanResponse>(`/v1/admin/drivers/${id}/ban`, { action, reason: reason ?? '' });
  },
  /** POST /v1/admin/drivers/:id/force-logout — revoke every active session for
   *  the driver via auth-svc token revocation. Does NOT change account status;
   *  the driver can log back in immediately. Gated server-side; audited. */
  forceLogoutDriver(id: number) {
    return postJSON<ForceLogoutResponse>(`/v1/admin/drivers/${id}/force-logout`, {});
  },
  /** PUT /v1/admin/drivers/:id/service-line — set a driver's service-line
   *  classification (the "type" column): premier | logistics | retail | reserve.
   *  Gated by drivers.service_line server-side; audited. */
  setDriverServiceLine(id: number, serviceLine: ServiceLine) {
    return putJSON<{ status: number; message?: string; service_line?: string }>(
      `/v1/admin/drivers/${id}/service-line`,
      { service_line: serviceLine },
    );
  },
  trips(params: { q?: string; customer_id?: number; driver_id?: number; limit?: number; offset?: number }, signal?: AbortSignal) {
    return getJSON<TripsResponse>('/v1/admin/trips', params, signal);
  },
  /** GET /v1/admin/trips/:id/detail — full trip + lifecycle timeline +
   *  participants + complaints (the trip detail drawer). Defensive: degrades to
   *  available:false when the trips table isn't in Postgres yet. */
  tripDetail(id: number, signal?: AbortSignal) {
    return getJSON<TripDetailResponse>(`/v1/admin/trips/${id}/detail`, undefined, signal);
  },
  /** POST /v1/admin/trips/:id/sign-media — presign a delivery-evidence image
   *  (sealed-package / delivered / signature) for viewing. The bucket is
   *  private; this mints a short-lived GET URL. Audited server-side. */
  signTripMedia(id: number, which: 'sealed' | 'delivered' | 'signature') {
    return postJSON<{ status: number; url: string; expires_in: number }>(
      `/v1/admin/trips/${id}/sign-media`,
      { which },
    );
  },
  // ── reserves (trips.read) — READ-ONLY list + roll-up over scheduled reserves
  //    (trip_type=14). trip-svc owns the table; releasing a reserve to a driver
  //    is a server-side scheduler/dispatch transition with no admin-assign
  //    endpoint to proxy, so this is list/view only (mirrors the legacy
  //    read-only Filament ReserveTripResource). Defensive: `available:false`
  //    until the trips table lands in this Postgres (pre-cutover). ──
  /** GET /v1/admin/reserves — list reserve trips + an upcoming/released/total
   *  roll-up. `released`: '' (all) | 'upcoming' (held) | 'released'. `q` matches
   *  trip code / pickup / drop address. */
  reserves(
    params: { q?: string; released?: string; limit?: number; offset?: number; trip_type?: number },
    signal?: AbortSignal,
  ) {
    return getJSON<ReservesResponse>('/v1/admin/reserves', params, signal);
  },
  /** POST /v1/admin/reserves/:id/assign — OFFER a held reserve to one driver.
   *  Proxies trip-svc; the driver then accepts/rejects it in their Bookings
   *  screen (and gets an FCM). Gated by reserves.assign server-side; audited. */
  assignReserveDriver(id: number, driverId: number) {
    return postJSON<{ status: number; message?: string; trip?: unknown }>(
      `/v1/admin/reserves/${id}/assign`,
      { driver_id: driverId },
    );
  },
  // ── Telride Boats: boat-captain + vessel management (dark until launch) ──
  /** POST /v1/admin/drivers/:id/boat-capable — flag/unflag a driver as a boat
   *  captain (additive to any car service-line). Gated by drivers.service_line. */
  setBoatCapable(driverId: number, boatCapable: boolean) {
    return postJSON<{ status: number; message?: string; boat_capable: boolean }>(
      `/v1/admin/drivers/${driverId}/boat-capable`,
      { boat_capable: boatCapable },
    );
  },
  /** GET /v1/admin/drivers/:id/vessels — the captain's boats + the current
   *  boat-capable flag (so the DriverDrawer toggle seeds from live state). */
  vessels(driverId: number, signal?: AbortSignal) {
    return getJSON<{ status: number; boat_capable: boolean; vessels: Vessel[] }>(
      `/v1/admin/drivers/${driverId}/vessels`,
      undefined,
      signal,
    );
  },
  /** POST /v1/admin/drivers/:id/vessels — create (id<=0) or update (id>0) a
   *  vessel. image_path/charter_cert_path are file_urls from the presign flow
   *  (reuse presignDriverDocument); omit to keep the existing pointer. */
  saveVessel(driverId: number, v: Partial<Vessel> & { name: string }) {
    return postJSON<{ status: number; message?: string; vessel: Vessel }>(
      `/v1/admin/drivers/${driverId}/vessels`,
      {
        id: v.id ?? 0,
        name: v.name,
        registration: v.registration ?? '',
        capacity: v.capacity ?? 0,
        image_path: v.image_path ?? null,
        charter_cert_path: v.charter_cert_path ?? null,
        insurance_expiry: v.insurance_expiry ?? '',
        status: v.status ?? 1,
      },
    );
  },
  /** POST /v1/admin/trips/:id/cancel — force-cancel a trip. NOT YET WIRED:
   *  returns 501 (ApiError) until trip-svc grows an internal admin-cancel
   *  endpoint to proxy. Gated by trips.cancel server-side. */
  cancelTrip(id: number, reason?: string) {
    return postJSON<CancelTripResponse>(`/v1/admin/trips/${id}/cancel`, { reason: reason ?? '' });
  },
  wallet(scope: 'customer' | 'driver', id: number, limit?: number, signal?: AbortSignal) {
    return getJSON<WalletResponse>(`/v1/admin/wallet/${scope}/${id}`, { limit }, signal);
  },
  /** MAKER: queue a manual wallet adjustment for approval (no money moves yet).
   *  A DIFFERENT super-admin must approve it before wallet-svc credits/debits. */
  adjustWallet(scope: 'customer' | 'driver', id: number, body: { direction: 'credit' | 'debit'; dollars: number; note?: string }) {
    return postJSON<AdjustResponse>(`/v1/admin/wallet/${scope}/${id}/adjust`, body);
  },
  /** List the maker-checker queue (status='pending') or history. */
  listWalletAdjustments(status?: 'pending' | 'applied' | 'rejected', signal?: AbortSignal) {
    return getJSON<WalletAdjustmentsResponse>('/v1/admin/wallet-adjustments', { status }, signal);
  },
  /** CHECKER: approve a pending adjustment → applies it (must differ from maker). */
  approveWalletAdjustment(id: number) {
    return postJSON<AdjustDecisionResponse>(`/v1/admin/wallet-adjustments/${id}/approve`, {});
  },
  /** CHECKER: reject a pending adjustment (no money moves). */
  rejectWalletAdjustment(id: number) {
    return postJSON<AdjustDecisionResponse>(`/v1/admin/wallet-adjustments/${id}/reject`, {});
  },
  fares(signal?: AbortSignal) {
    return getJSON<FaresResponse>('/v1/admin/fares', undefined, signal);
  },
  /** GET /v1/admin/payments — Stripe payment history (card top-ups + their
   *  refund/dispute clawbacks), read from wallet-svc's wallet_ledger. Optional
   *  filters: kind ('topup'|'refund'|'dispute'), customer_id, and q (free-text
   *  against the Stripe reference / rider name / phone / email). Defensive:
   *  degrades to available:false when the wallet ledger isn't in Postgres yet. */
  payments(
    params: { kind?: string; customer_id?: number; q?: string; limit?: number; offset?: number },
    signal?: AbortSignal,
  ) {
    return getJSON<PaymentsResponse>('/v1/admin/payments', params, signal);
  },
  /** GET /v1/admin/payments/:ref — one payment by its RAW ledger ref (the Stripe
   *  PaymentIntent id for a top-up, or 'refund:<charge>'/'dispute:<charge>' for a
   *  clawback). The ref is URL-encoded for the path. */
  payment(ref: string, signal?: AbortSignal) {
    return getJSON<PaymentDetailResponse>(`/v1/admin/payments/${encodeURIComponent(ref)}`, undefined, signal);
  },
  /** GET /v1/admin/driver-tips — read-only audit log of tips riders sent drivers
   *  (wallet → wallet), read from earnings-svc's driver_tips ledger. Optional
   *  filters: driver_id, customer_id, and q (free-text against the trip code /
   *  either party's name / phone). Defensive: degrades to available:false when
   *  the driver_tips table isn't in Postgres yet. */
  driverTips(
    params: { driver_id?: number; customer_id?: number; q?: string; limit?: number; offset?: number },
    signal?: AbortSignal,
  ) {
    return getJSON<DriverTipsResponse>('/v1/admin/driver-tips', params, signal);
  },
  /** GET /v1/admin/driver-tips/:id — one tip by its driver_tips primary-key id. */
  driverTip(id: number, signal?: AbortSignal) {
    return getJSON<DriverTipDetailResponse>(`/v1/admin/driver-tips/${id}`, undefined, signal);
  },
  /** GET /v1/admin/payouts — Azupay driver earnings cash-outs, read from
   *  wallet-svc's driver_payouts. Optional filters: status (one of the Azupay
   *  statuses), driver_id, and q (free-text against the Azupay payment id /
   *  driver name / phone / email). Defensive: degrades to available:false when
   *  the driver_payouts table isn't in Postgres yet. */
  payouts(
    params: { status?: string; driver_id?: number; q?: string; limit?: number; offset?: number },
    signal?: AbortSignal,
  ) {
    return getJSON<PayoutsResponse>('/v1/admin/payouts', params, signal);
  },
  // ── driver expenses (reimbursements) — expenses.read to list, expenses.manage
  //    to approve/reject. admin-svc reads earnings-svc's driver_expenses + flips
  //    the status directly. ──
  /** GET /v1/admin/driver-expenses — list driver expense reports. */
  driverExpenses(
    params: { status?: string; driver_id?: number; q?: string; limit?: number; offset?: number },
    signal?: AbortSignal,
  ) {
    return getJSON<DriverExpensesResponse>('/v1/admin/driver-expenses', params, signal);
  },
  /** GET /v1/admin/driver-expenses/:id — one expense report. */
  driverExpense(id: number, signal?: AbortSignal) {
    return getJSON<DriverExpenseDetailResponse>(`/v1/admin/driver-expenses/${id}`, undefined, signal);
  },
  /** POST /v1/admin/driver-expenses/:id/status — approve (1) / reject (2) /
   *  re-pend (0). Gated by expenses.manage; audited. */
  setExpenseStatus(id: number, status: 0 | 1 | 2) {
    return postJSON<UpdateExpenseStatusResponse>(`/v1/admin/driver-expenses/${id}/status`, { status });
  },
  /** GET /v1/admin/driver-earnings — the per-trip driver earnings ledger, read
   *  from earnings-svc's driver_trip_earnings (one row per completed trip;
   *  amount = fare + tip). Optional filters: driver_id, and q (free-text against
   *  the trip id / driver name / phone / email / pickup-drop suburb). Defensive:
   *  degrades to available:false when the table isn't in Postgres yet. */
  driverEarnings(
    params: { driver_id?: number; q?: string; limit?: number; offset?: number },
    signal?: AbortSignal,
  ) {
    return getJSON<DriverEarningsResponse>('/v1/admin/driver-earnings', params, signal);
  },
  /** POST /v1/admin/drivers/:id/payout-kyc/verify — mark (action 'verify') or
   *  revert (action 'unverify') a driver's payout-KYC (their bank/PayID record,
   *  which gates Azupay cash-outs). admin-svc owns the migration-037 verification
   *  columns and flips them directly; gated by payouts.kyc_verify + audited.
   *  Defensive: a clean 503 (ApiError) until the driver_kyc table is in this
   *  Postgres and migration 037 is applied. note is recorded in the audit trail. */
  verifyPayoutKYC(driverId: number, action: 'verify' | 'unverify' = 'verify', note?: string) {
    return postJSON<PayoutKYCVerifyResponse>(`/v1/admin/drivers/${driverId}/payout-kyc/verify`, {
      action,
      note: note ?? '',
    });
  },
  /** GET /v1/admin/dashboard/summary — the platform-stats roll-up the Dashboard
   *  landing page shows (driver/customer/trip counts, today's revenue, wallet
   *  pools, open complaint/support queues). Gated by dashboard.read server-side
   *  (all roles hold it). Every figure fails soft to 0, so this never errors on a
   *  partially-migrated Postgres — the card just shows zeros for absent sources. */
  dashboardSummary(signal?: AbortSignal) {
    return getJSON<DashboardSummary>('/v1/admin/dashboard/summary', undefined, signal);
  },
  /** GET /v1/admin/me — current staffer + their role's permissions. Used to
   *  refresh the gated UI's permission set after login / on reload. */
  me(signal?: AbortSignal) {
    return getJSON<MeResponse>('/v1/admin/me', undefined, signal);
  },
  complaints(params: { q?: string; customer_id?: number; limit?: number; offset?: number }, signal?: AbortSignal) {
    return getJSON<ComplaintsResponse>('/v1/admin/complaints', params, signal);
  },
  /** POST /v1/admin/complaints/:id/status — advance a complaint's back-office
   *  workflow status. Proxies comms-svc (which owns the `complaints` table).
   *  Gated by complaints.manage server-side; audited. Degrades to a clean 503
   *  (ApiError) if admin-svc isn't configured to reach comms-svc yet. */
  updateComplaintStatus(id: number, status: number, note?: string) {
    return postJSON<ComplaintStatusResponse>(`/v1/admin/complaints/${id}/status`, { status, note: note ?? '' });
  },
  supportThreads(params: { status?: string; customer_id?: number; limit?: number; offset?: number }, signal?: AbortSignal) {
    return getJSON<SupportThreadsResponse>('/v1/admin/support/threads', params, signal);
  },
  supportThread(customerId: number, signal?: AbortSignal) {
    return getJSON<SupportThreadDetail>(`/v1/admin/support/threads/${customerId}`, undefined, signal);
  },
  /** POST /v1/admin/support/threads/:id/reply — post an agent reply into a
   *  customer's support thread. id is the CUSTOMER id (one thread per customer).
   *  Proxies support-svc (which owns the threads/messages tables) carrying the
   *  acting staffer as the agent identity, so the customer sees the reply in
   *  real time. Gated by support.manage server-side; audited. Degrades to a
   *  clean 503 (ApiError) until admin-svc can reach support-svc + the tables
   *  land in Postgres. clientToken (optional) makes the send idempotent. */
  supportReply(customerId: number, body: string, clientToken?: string) {
    return postJSON<SupportReplyResponse>(`/v1/admin/support/threads/${customerId}/reply`, {
      body,
      client_token: clientToken ?? '',
    });
  },
  /** POST /v1/admin/support/threads/:id/claim — assign the thread to the acting
   *  staffer (support-svc records assigned_agent_id). Idempotent. Gated by
   *  support.manage server-side; audited. */
  supportClaim(customerId: number) {
    return postJSON<SupportClaimResponse>(`/v1/admin/support/threads/${customerId}/claim`, {});
  },
  /** POST /v1/admin/support/threads/:id/close — set the thread's status. Pass
   *  'closed' to resolve (the default) or 'open' to reopen. Gated by
   *  support.manage server-side; audited. */
  supportClose(customerId: number, status: 'open' | 'closed' = 'closed') {
    return postJSON<SupportCloseResponse>(`/v1/admin/support/threads/${customerId}/close`, { status });
  },
  campaigns(params: { active?: number; limit?: number; offset?: number }, signal?: AbortSignal) {
    return getJSON<CampaignsResponse>('/v1/admin/incentives/campaigns', params, signal);
  },
  /** POST /v1/admin/incentives/campaigns — author a new driver quest/campaign.
   *  Proxies incentives-svc (which owns the `incentive_campaigns` table). Gated
   *  by incentives.manage server-side; audited. Degrades to a clean 503
   *  (ApiError) if admin-svc isn't configured to reach incentives-svc yet. */
  createCampaign(input: CampaignInput) {
    return postJSON<CampaignMutationResponse>('/v1/admin/incentives/campaigns', input);
  },
  /** PUT /v1/admin/incentives/campaigns/:id — edit an existing campaign (full
   *  overwrite of its editable fields). Same proxy/gating/audit as create; a
   *  missing id yields a 404 (ApiError). */
  updateCampaign(id: number, input: CampaignInput) {
    return putJSON<CampaignMutationResponse>(`/v1/admin/incentives/campaigns/${id}`, input);
  },
  driverTiers(params: { limit?: number; offset?: number }, signal?: AbortSignal) {
    return getJSON<DriverTiersResponse>('/v1/admin/loyalty/tiers', params, signal);
  },
  streaks(params: { active?: number; q?: string; limit?: number; offset?: number }, signal?: AbortSignal) {
    return getJSON<StreaksResponse>('/v1/admin/incentives/streaks', params, signal);
  },

  // ── promo codes (incentives-svc owns promo_codes; promos.read / .manage) ──
  /** GET /v1/admin/promos — list customer promo codes (ALL statuses; the rider
   *  app only sees active ones). Optional filters: active (1 = active only) and q
   *  (free-text against code / name / description). Defensive: degrades to
   *  available:false when the promo_codes table isn't in Postgres yet. */
  promos(params: { active?: number; q?: string; limit?: number; offset?: number }, signal?: AbortSignal) {
    return getJSON<PromosResponse>('/v1/admin/promos', params, signal);
  },
  /** GET /v1/admin/promos/:id — one promo code (any status). */
  promo(id: number, signal?: AbortSignal) {
    return getJSON<{ status: number; available: boolean; promo?: Promo; note?: string }>(
      `/v1/admin/promos/${id}`,
      undefined,
      signal,
    );
  },
  /** POST /v1/admin/promos — author a new promo code. Proxies incentives-svc
   *  (which owns `promo_codes`). Gated by promos.manage server-side; audited.
   *  Degrades to a clean 503 (ApiError) if admin-svc isn't configured to reach
   *  incentives-svc yet. */
  createPromo(input: PromoInput) {
    return postJSON<PromoMutationResponse>('/v1/admin/promos', input);
  },
  /** PUT /v1/admin/promos/:id — edit a promo code (full overwrite of its editable
   *  fields). Same proxy/gating/audit as create; a missing id yields a 404
   *  (ApiError). */
  updatePromo(id: number, input: PromoInput) {
    return putJSON<PromoMutationResponse>(`/v1/admin/promos/${id}`, input);
  },
  /** POST /v1/admin/promos/:id/status — activate (status:1) or deactivate
   *  (status:0) a promo without re-posting the whole row. Gated by promos.manage
   *  server-side; audited. */
  setPromoStatus(id: number, status: 0 | 1) {
    return postJSON<PromoMutationResponse>(`/v1/admin/promos/${id}/status`, { status });
  },
  /** GET /v1/admin/audit — the immutable staff action trail (admin_audit_log).
   *  Filterable by free text (actor/action/target/IP), exact action, or result;
   *  paginated with a total count. Each row carries the full detail + request
   *  metadata (IP / user-agent / method / route). */
  audit(
    params: { limit?: number; offset?: number; q?: string; action?: string; result?: string },
    signal?: AbortSignal,
  ) {
    return getJSON<AuditResponse>('/v1/admin/audit', params, signal);
  },

  /** GET /v1/admin/otp-codes — recent TEST-NUMBER OTP login codes ("God View")
   *  so staff can read codes during testing without box access. dashboard.read. */
  otpCodes(signal?: AbortSignal) {
    return getJSON<OtpCodesResponse>('/v1/admin/otp-codes', {}, signal);
  },

  /** GET /v1/admin/push-log — recent FCM push attempts + results (godview) so
   *  staff can diagnose delivery: who, type, token-present, success/failure, and
   *  the real FCM/APNs reason. notifications.read. */
  pushLog(limit = 100, signal?: AbortSignal) {
    return getJSON<PushLogResponse>('/v1/admin/push-log', { limit: String(limit) }, signal);
  },

  /** GET /v1/admin/verification-log — recent driver selfie ID-verification
   *  attempts + results (who, when, pass/fail, score, consent). drivers.read.
   *  Optional subject filter to scope to one driver. */
  verificationLog(
    limit = 100,
    opts?: { subject_type?: string; subject_id?: number },
    signal?: AbortSignal,
  ) {
    const params: Record<string, string> = { limit: String(limit) };
    if (opts?.subject_type) params.subject_type = opts.subject_type;
    if (opts?.subject_id) params.subject_id = String(opts.subject_id);
    return getJSON<VerificationLogResponse>('/v1/admin/verification-log', params, signal);
  },

  // ── staff management (admin_users — superadmin-only / staff.manage) ──
  /** GET /v1/admin/staff — list/search admin users (no password material). */
  staff(params: { q?: string; limit?: number; offset?: number }, signal?: AbortSignal) {
    return getJSON<StaffListResponse>('/v1/admin/staff', params, signal);
  },
  /** POST /v1/admin/staff — create a new staff user with a role. */
  createStaff(body: { email: string; name?: string; role: string; password: string }) {
    return postJSON<CreateStaffResponse>('/v1/admin/staff', body);
  },
  /** PATCH /v1/admin/staff/:id — change role and/or enable/disable. */
  updateStaff(id: number, body: { role?: string; status?: 'active' | 'disabled' }) {
    return patchJSON<StaffMutationResponse>(`/v1/admin/staff/${id}`, body);
  },
  /** POST /v1/admin/staff/:id/password — reset a staffer's password. */
  resetStaffPassword(id: number, password: string) {
    return postJSON<ResetStaffPasswordResponse>(`/v1/admin/staff/${id}/password`, { password });
  },

  // ── roles + permissions (staff.manage — DB-backed custom roles, migration 041) ──
  /** GET /v1/admin/roles — every role (system + custom) with counts. */
  roles(signal?: AbortSignal) {
    return getJSON<RolesListResponse>('/v1/admin/roles', undefined, signal);
  },
  /** GET /v1/admin/roles/:name — one role + its effective permission set. */
  role(name: string, signal?: AbortSignal) {
    return getJSON<{ status: number; role: RoleDetail }>(`/v1/admin/roles/${encodeURIComponent(name)}`, undefined, signal);
  },
  /** GET /v1/admin/permissions — the catalogue the role builder picks from. */
  permissions(signal?: AbortSignal) {
    return getJSON<{ status: number; permissions: PermissionInfo[] }>('/v1/admin/permissions', undefined, signal);
  },
  /** POST /v1/admin/roles — create a custom role. */
  createRole(body: { name: string; description: string; permissions: string[] }) {
    return postJSON<{ status: number; message?: string; name?: string }>('/v1/admin/roles', body);
  },
  /** PUT /v1/admin/roles/:name — edit a custom role's description + permissions. */
  updateRole(name: string, body: { description: string; permissions: string[] }) {
    return putJSON<{ status: number; message?: string }>(`/v1/admin/roles/${encodeURIComponent(name)}`, body);
  },
  /** DELETE /v1/admin/roles/:name — delete an unused custom role. */
  deleteRole(name: string) {
    return deleteJSON<{ status: number; message?: string }>(`/v1/admin/roles/${encodeURIComponent(name)}`);
  },

  // ── CMS / content (cms.read for the lists; cms.manage for the writes —
  //    admin-svc proxies content-svc, which owns the tables) ──
  /** GET /v1/admin/cms/faqs — list FAQs (both langs, all statuses). scope filters
   *  the audience ('customer' = user_type_id 1, 'driver' = 2; omit for both).
   *  Defensive: degrades to available:false when the `faqs` table isn't in this
   *  Postgres yet. */
  cmsFaqs(scope?: 'customer' | 'driver', signal?: AbortSignal) {
    return getJSON<CMSFaqsResponse>('/v1/admin/cms/faqs', { scope }, signal);
  },
  /** POST /v1/admin/cms/faqs — author a new FAQ. Proxies content-svc; gated by
   *  cms.manage server-side; audited. Degrades to a clean 503 (ApiError) when
   *  admin-svc can't reach content-svc yet (CONTENT_INTERNAL_SECRET unset). */
  createFaq(input: CMSFaqInput) {
    return postJSON<{ status: number; message?: string; faq?: CMSFaq }>('/v1/admin/cms/faqs', input);
  },
  /** PUT /v1/admin/cms/faqs/:id — edit a FAQ (full overwrite of its editable
   *  fields). Same proxy/gating/audit as create; a missing id yields a 404. */
  updateFaq(id: number, input: CMSFaqInput) {
    return putJSON<{ status: number; message?: string; faq?: CMSFaq }>(`/v1/admin/cms/faqs/${id}`, input);
  },
  /** DELETE /v1/admin/cms/faqs/:id — delete a FAQ. */
  deleteFaq(id: number) {
    return deleteJSON<{ status: number; message?: string }>(`/v1/admin/cms/faqs/${id}`);
  },
  /** GET /v1/admin/cms/policies — list legal/about policies (same scope filter +
   *  defensive behaviour as cmsFaqs). */
  cmsPolicies(scope?: 'customer' | 'driver', signal?: AbortSignal) {
    return getJSON<CMSPoliciesResponse>('/v1/admin/cms/policies', { scope }, signal);
  },
  /** POST /v1/admin/cms/policies — author a new policy. */
  createPolicy(input: CMSPolicyInput) {
    return postJSON<{ status: number; message?: string; policy?: CMSPolicy }>('/v1/admin/cms/policies', input);
  },
  /** PUT /v1/admin/cms/policies/:id — edit a policy. */
  updatePolicy(id: number, input: CMSPolicyInput) {
    return putJSON<{ status: number; message?: string; policy?: CMSPolicy }>(`/v1/admin/cms/policies/${id}`, input);
  },
  /** DELETE /v1/admin/cms/policies/:id — delete a policy. */
  deletePolicy(id: number) {
    return deleteJSON<{ status: number; message?: string }>(`/v1/admin/cms/policies/${id}`);
  },
  /** GET /v1/admin/cms/app-settings — the app_settings singleton. available=false
   *  ⇒ table absent; configured=false ⇒ table present but no row seeded yet. */
  cmsAppSettings(signal?: AbortSignal) {
    return getJSON<CMSAppSettingsResponse>('/v1/admin/cms/app-settings', undefined, signal);
  },
  /** PUT /v1/admin/cms/app-settings — UPSERT the app_settings singleton (the
   *  portal posts the whole row). Proxies content-svc; gated by cms.manage. */
  updateAppSettings(input: CMSAppSettings) {
    return putJSON<{ status: number; message?: string; app_settings?: CMSAppSettings }>(
      '/v1/admin/cms/app-settings',
      input,
    );
  },
  /** PUT /v1/admin/cms/app-version — publish the latest build + force-update kill
   *  switch for ONE platform (1 = Android, 2 = iOS) AND app (user_type_id: 1 =
   *  customer, 2 = driver). version_code is the build number the apps compare
   *  against; force_update (0|1) hard-blocks older builds when 1. Proxies
   *  content-svc; gated by cms.manage server-side; audited. */
  updateAppVersion(input: {
    platform: number;
    user_type_id: number;
    version_number: string;
    version_code: string;
    force_update: number;
  }) {
    return putJSON<{ status: number; message?: string }>('/v1/admin/cms/app-version', input);
  },
  /** GET /v1/admin/cms/app-version — reads the currently-published builds for
   *  each platform × app (customer + driver) to SEED the force-update forms.
   *  Proxies content-svc through admin-svc (gated by cms.manage server-side, same
   *  as the write); the admin endpoint degrades to null rows so this never
   *  hard-errors the page. Returns nulls when nothing is published. */
  async cmsAppVersions(signal?: AbortSignal): Promise<CMSAppVersionsResponse> {
    const res = await getJSON<AdminAppVersionsResponse>('/v1/admin/cms/app-version', undefined, signal);
    return {
      android: res.result?.android ?? null,
      ios: res.result?.ios ?? null,
      android_driver: res.result?.android_driver ?? null,
      ios_driver: res.result?.ios_driver ?? null,
    };
  },
  /** GET /v1/admin/cms/contact — the contact_settings singleton. */
  cmsContact(signal?: AbortSignal) {
    return getJSON<CMSContactResponse>('/v1/admin/cms/contact', undefined, signal);
  },
  /** PUT /v1/admin/cms/contact — UPSERT the contact_settings singleton. */
  updateContact(input: CMSContact) {
    return putJSON<{ status: number; message?: string; contact?: CMSContact }>('/v1/admin/cms/contact', input);
  },

  // ── operating zones (cms.read for the list; cms.manage for the writes —
  //    admin-svc proxies content-svc for BOTH the list and the writes) ──
  /** GET /v1/admin/zones — list every operating zone (full detail). Defensive:
   *  degrades to available:false when admin-svc can't reach content-svc's zones
   *  admin endpoint yet. */
  zones(signal?: AbortSignal) {
    return getJSON<ZonesResponse>('/v1/admin/zones', undefined, signal);
  },
  /** POST /v1/admin/zones — create a zone. Proxies content-svc; gated by
   *  cms.manage server-side; audited. Degrades to a clean 503 (ApiError) until the
   *  content secret is wired. */
  createZone(input: ZoneInput) {
    return postJSON<ZoneMutationResponse>('/v1/admin/zones', input);
  },
  /** PUT /v1/admin/zones/:id — edit a zone (full overwrite of editable fields). */
  updateZone(id: number, input: ZoneInput) {
    return putJSON<ZoneMutationResponse>(`/v1/admin/zones/${id}`, input);
  },
  /** DELETE /v1/admin/zones/:id — delete a zone. */
  deleteZone(id: number) {
    return deleteJSON<ZoneMutationResponse>(`/v1/admin/zones/${id}`);
  },
  // ── Telride Boats docks (docks.read list / docks.manage writes — admin-svc
  //    owns docks + dock_distances directly). DARK until launch; degrades to a
  //    clean available:false until migration 056 lands. No DELETE — v1
  //    deactivates a dock via updateDock({is_active:false}). ──
  /** GET /v1/admin/docks — list docks (active + inactive). */
  docks(signal?: AbortSignal) {
    return getJSON<DocksResponse>('/v1/admin/docks', undefined, signal);
  },
  /** POST /v1/admin/docks — create a dock. Gated by docks.manage; audited. */
  createDock(input: DockInput) {
    return postJSON<DockMutationResponse>('/v1/admin/docks', input);
  },
  /** PUT /v1/admin/docks/:id — edit a dock (full overwrite; deactivate via
   *  is_active:false). */
  updateDock(id: number, input: DockInput) {
    return putJSON<DockMutationResponse>(`/v1/admin/docks/${id}`, input);
  },
  /** GET /v1/admin/dock-distances — the per-pair billable-km matrix. */
  dockDistances(signal?: AbortSignal) {
    return getJSON<DockDistancesResponse>('/v1/admin/dock-distances', undefined, signal);
  },
  /** POST /v1/admin/dock-distances — upsert one ordered (from,to) pair's km. */
  setDockDistance(fromDockId: number, toDockId: number, distanceKm: number) {
    return postJSON<DockMutationResponse>('/v1/admin/dock-distances', {
      from_dock_id: fromDockId,
      to_dock_id: toDockId,
      distance_km: distanceKm,
    });
  },
  // ── EV charging stations (cms.read list / cms.manage writes — admin-svc owns
  //    the `chargers` table directly). The driver app lists active chargers. ──
  /** GET /v1/admin/chargers — list all charging stations (active + inactive). */
  chargers(signal?: AbortSignal) {
    return getJSON<ChargersResponse>('/v1/admin/chargers', undefined, signal);
  },
  /** POST /v1/admin/chargers — create a charging station. cms.manage; audited. */
  createCharger(input: ChargerInput) {
    return postJSON<Charger>('/v1/admin/chargers', input);
  },
  /** PUT /v1/admin/chargers/:id — edit a charging station (full overwrite). */
  updateCharger(id: number, input: ChargerInput) {
    return putJSON<Charger>(`/v1/admin/chargers/${id}`, input);
  },
  /** DELETE /v1/admin/chargers/:id — remove a charging station. */
  deleteCharger(id: number) {
    return deleteJSON<{ status: number }>(`/v1/admin/chargers/${id}`);
  },
  // ── Speeding events (proof when a customer reports a driver was speeding —
  //    safety-svc records them; admin-svc reads the shared table). ──
  /** GET /v1/admin/speeding — recorded speeding events, newest-first, optionally
   *  filtered to one driver or trip. */
  speeding(params: { driver_id?: number; trip_id?: number; limit?: number }, signal?: AbortSignal) {
    return getJSON<SpeedingResponse>('/v1/admin/speeding', params, signal);
  },
  // ── notifications (notifications.read for the history; notifications.send for
  //    the compose — admin-svc proxies notification-svc's admin-send endpoint) ──
  /** GET /v1/admin/notifications/history — recent admin-sent broadcasts,
   *  newest-first, sourced from the staff audit trail. */
  notificationHistory(params: { limit?: number }, signal?: AbortSignal) {
    return getJSON<NotificationHistoryResponse>('/v1/admin/notifications/history', params, signal);
  },
  /** POST /v1/admin/notifications/send — compose + send a push to an audience
   *  (all customers, all drivers, or one user). Proxies notification-svc (which
   *  owns FCM + the in-app feed); gated by notifications.send server-side;
   *  audited. Degrades to a clean 503 (ApiError) when admin-svc can't reach
   *  notification-svc yet (NOTIFICATION_INTERNAL_SECRET unset). */
  sendNotification(input: SendNotificationInput) {
    return postJSON<SendNotificationResponse>('/v1/admin/notifications/send', input);
  },

  // ── driver boosts (incentives-svc owns driver_boosts — boosts.read; READ-ONLY) ──
  /** GET /v1/admin/boosts — list driver boosts (ALL statuses; the driver
   *  Opportunities read is active-only). Optional filters: active (1 = active
   *  only) and q (free-text against the zone name). Defensive: degrades to
   *  available:false when the driver_boosts table isn't in this Postgres yet. */
  boosts(params: { active?: number; q?: string; limit?: number; offset?: number }, signal?: AbortSignal) {
    return getJSON<BoostsResponse>('/v1/admin/boosts', params, signal);
  },
  /** GET /v1/admin/boosts/stats — the headline totals (count / active / top
   *  active bonus / total opt-ins) shown above the table. */
  boostStats(signal?: AbortSignal) {
    return getJSON<BoostStatsResponse>('/v1/admin/boosts-stats', undefined, signal);
  },
  /** GET /v1/admin/boosts/:id — one boost (any status). */
  boost(id: number, signal?: AbortSignal) {
    return getJSON<{ status: number; available: boolean; boost?: Boost; note?: string }>(
      `/v1/admin/boosts/${id}`,
      undefined,
      signal,
    );
  },

  // ── quests (incentives.read) — READ-ONLY list/detail over driver_quests
  //    (incentives-svc owns the table; it exposes no admin quest-authoring
  //    endpoint, so there's no write to proxy). Defensive: `available:false`
  //    until incentives-svc migration 025 lands in this Postgres. ──
  /** GET /v1/admin/quests — list driver quests (all statuses; `active=1`
   *  filters to active only). */
  quests(params: { active?: number; limit?: number; offset?: number }, signal?: AbortSignal) {
    return getJSON<QuestsResponse>('/v1/admin/quests', params, signal);
  },
  /** GET /v1/admin/quests/:id — one driver quest (any status). */
  quest(id: number, signal?: AbortSignal) {
    return getJSON<QuestDetailResponse>(`/v1/admin/quests/${id}`, undefined, signal);
  },

  // ── quest tiers (incentives.read) — READ-ONLY per-quest bonus ladder ──
  /** GET /v1/admin/incentives/quest-tiers — list the per-quest bonus tiers (each
   *  quest's ordered ladder of trip-target → bonus). Optional filter: quest_id
   *  (scope to one quest; omit for all). Also returns the set of quests-with-tiers
   *  for the filter dropdown. Read-only (incentives-svc owns the table; no admin
   *  authoring endpoint yet). Defensive: degrades to available:false when the
   *  driver_quest_tiers table isn't in this Postgres yet. */
  questTiers(params: { quest_id?: number; limit?: number; offset?: number }, signal?: AbortSignal) {
    return getJSON<QuestTiersResponse>('/v1/admin/incentives/quest-tiers', params, signal);
  },
  /** GET /v1/admin/incentives/quest-tiers/:id — one quest tier (with its quest
   *  title), for the detail drawer. */
  questTier(id: number, signal?: AbortSignal) {
    return getJSON<QuestTierDetailResponse>(`/v1/admin/incentives/quest-tiers/${id}`, undefined, signal);
  },

  // ── quest opt-ins (incentives.read) — READ-ONLY per-driver opt-ins + progress ──
  /** GET /v1/admin/quest-optins — per-driver opt-ins + progress for the weekly
   *  driver quests. incentives-svc owns `driver_quest_optins`; admin-svc only
   *  SELECTs it (read-only — drivers create opt-ins in-app, progress is advanced
   *  off the trip stream, and incentives-svc exposes no admin write). Optional
   *  quest_id scopes to one quest; q free-text matches the driver name / quest
   *  title. Gated by incentives.read server-side. Defensive: available:false when
   *  the table isn't in this Postgres yet. */
  questOptins(
    params: { q?: string; quest_id?: number; limit?: number; offset?: number },
    signal?: AbortSignal,
  ) {
    return getJSON<QuestOptinsResponse>('/v1/admin/quest-optins', params, signal);
  },
  /** GET /v1/admin/quest-optins/stats — headline totals (opt-ins, distinct
   *  drivers, distinct quests, total trips completed) for the page's stat tiles.
   *  Gated by incentives.read server-side. Defensive: available:false when the
   *  table isn't in this Postgres yet. */
  questOptinStats(signal?: AbortSignal) {
    return getJSON<QuestOptinStatsResponse>('/v1/admin/quest-optins-stats', undefined, signal);
  },

  // ── guarantee enrollments (incentives.read) — READ-ONLY per-driver enrollments + progress ──
  /** GET /v1/admin/guarantee-enrollments — per-driver enrollment + progress for
   *  the weekly earnings guarantees. incentives-svc owns
   *  `driver_guarantee_enrollments`; admin-svc only SELECTs it (read-only —
   *  drivers are auto-enrolled in-app, progress is advanced off the trip stream,
   *  and incentives-svc exposes no admin write). Optional guarantee_id scopes to
   *  one guarantee; q free-text matches the driver name / period label. Gated by
   *  incentives.read server-side. Defensive: available:false when the table isn't
   *  in this Postgres yet. */
  guaranteeEnrollments(
    params: { q?: string; guarantee_id?: number; limit?: number; offset?: number },
    signal?: AbortSignal,
  ) {
    return getJSON<GuaranteeEnrollmentsResponse>('/v1/admin/guarantee-enrollments', params, signal);
  },
  /** GET /v1/admin/guarantee-enrollments/stats — headline totals (enrollments,
   *  distinct drivers, distinct guarantees, total earnings tracked so far) for the
   *  page's stat tiles. Gated by incentives.read server-side. Defensive:
   *  available:false when the table isn't in this Postgres yet. */
  guaranteeEnrollmentStats(signal?: AbortSignal) {
    return getJSON<GuaranteeEnrollmentStatsResponse>('/v1/admin/guarantee-enrollments-stats', undefined, signal);
  },

  // ── driver payout-KYC (driver bank / PayID records — driver-svc owns them) ──
  /** GET /v1/admin/payout-kyc — driver bank / PayID records that gate Azupay
   *  cash-outs, read from driver-svc's driver_kyc. Optional filters: facet
   *  ('payid' | 'bank' | 'incomplete'), q (free-text against the driver's
   *  name / phone / email or the bank name / account / BSB / PayID), and order
   *  ('oldest' | 'recent' — FIFO review-queue ordering for the Bank-KYC queue).
   *  Defensive: degrades to available:false when the driver_kyc table isn't in
   *  Postgres yet. The verify WRITE is adminApi.verifyPayoutKYC (gated by
   *  payouts.kyc_verify + audited; admin-svc owns the migration-037 verification
   *  columns). */
  payoutKYC(
    params: { facet?: string; q?: string; order?: string; limit?: number; offset?: number },
    signal?: AbortSignal,
  ) {
    return getJSON<PayoutKYCResponse>('/v1/admin/payout-kyc', params, signal);
  },

  // ── fare rates (fares.read for the table; fares.manage for the per-category
  //    edit — admin-svc owns the `fare_rates` table + audits the write) ──
  /** GET /v1/admin/fares — the per-category km-credit rate table (one row per
   *  trip type) the legacy Filament FareRateResource showed: the buying rate +
   *  commission (GLOBAL, read-only), the time-banded customer/driver per-km
   *  rates, and the trip-type catalogue. editable=true once the per-category
   *  `fare_rates` table (migration 036) is present. Gated by fares.read; fails
   *  soft (configured=false) when trip_settings isn't in this Postgres yet. */
  fareRates(signal?: AbortSignal) {
    return getJSON<FareRatesResponse>('/v1/admin/fares', undefined, signal);
  },
  /** PUT /v1/admin/fares/:trip_type_id/rates — overwrite ONE category's
   *  time-banded customer + driver per-km rates (the per-category `fare_rates`
   *  row). Gated by fares.manage (finance + superadmin); admin-svc audits the
   *  write + 503s cleanly when migration 036 isn't applied. The global buying
   *  rate + commission are NOT editable here (trip-svc owns trip_settings). */
  updateFareRate(tripTypeId: number, input: FareRateInput) {
    return putJSON<FareRateUpdateResponse>(`/v1/admin/fares/${tripTypeId}/rates`, input);
  },

  /** GET /v1/admin/destination-settings — the driver "Going Home" daily limit + on/off. */
  destinationSettings(signal?: AbortSignal) {
    return getJSON<DestinationSettingsResponse>('/v1/admin/destination-settings', undefined, signal);
  },
  /** PUT /v1/admin/destination-settings — set the daily destination-mode limit + enabled flag. */
  updateDestinationSettings(input: { daily_limit: number; enabled: boolean }) {
    return putJSON<DestinationSettingsResponse>('/v1/admin/destination-settings', input);
  },

  /** GET /v1/admin/feature-settings — platform feature toggles (split / masked calling / wait-time). */
  featureSettings(signal?: AbortSignal) {
    return getJSON<FeatureSettingsResponse>('/v1/admin/feature-settings', undefined, signal);
  },
  /** PUT /v1/admin/feature-settings — flip the toggles + set the wait-time price/grace. */
  updateFeatureSettings(input: {
    split_fare_enabled: boolean;
    masked_calling_enabled: boolean;
    wait_time_enabled: boolean;
    wait_time_price_per_min: number;
    wait_time_grace_min: number;
    pickup_surge_enabled: boolean;
    pickup_surge_flat_day_rate: boolean;
    short_ride_surge_flat_day_rate: boolean;
  }) {
    return putJSON<FeatureSettingsResponse>('/v1/admin/feature-settings', input);
  },
  // ── Customer membership tiers (cms.read list / cms.manage write — admin-svc
  //    owns the membership_tier_settings singleton; wallet-svc reads it live). ──
  /** GET /v1/admin/membership-tiers — the ladder + the live tier distribution. */
  membershipTiers(signal?: AbortSignal) {
    return getJSON<MembershipTiersResponse>('/v1/admin/membership-tiers', undefined, signal);
  },
  /** PUT /v1/admin/membership-tiers — edit the thresholds / discount % / enabled. */
  updateMembershipTiers(input: MembershipTiersInput) {
    return putJSON<MembershipTiersResponse>('/v1/admin/membership-tiers', input);
  },

  // ── trip types (cms.read for the live list/detail; cms.manage for the writes —
  //    admin-svc reads `trip_types` directly + proxies content-svc for the writes) ──
  /** GET /v1/admin/trip-types — list the service-tier catalogue (all statuses,
   *  sorted by `sort`). Optional filters: q (free-text against the name) and
   *  status (1 = active only, 0 = inactive only). The READ is live (the
   *  table is in this Postgres). */
  tripTypes(params: { q?: string; status?: number; limit?: number; offset?: number }, signal?: AbortSignal) {
    return getJSON<TripTypesResponse>('/v1/admin/trip-types', params, signal);
  },
  /** GET /v1/admin/trip-types/:id — one trip type (any status). */
  tripType(id: number, signal?: AbortSignal) {
    return getJSON<{ status: number; available: boolean; trip_type?: TripType; note?: string }>(
      `/v1/admin/trip-types/${id}`,
      undefined,
      signal,
    );
  },
  /** POST /v1/admin/trip-types — author a new trip type. Proxies content-svc;
   *  gated by cms.manage server-side; audited. Degrades to a clean 503 (ApiError)
   *  until content-svc's internal trip-type admin endpoints + the content secret
   *  are wired. */
  createTripType(input: TripTypeInput) {
    return postJSON<TripTypeMutationResponse>('/v1/admin/trip-types', input);
  },
  /** PUT /v1/admin/trip-types/:id — edit a trip type (full overwrite of its
   *  editable fields). Same proxy/gating/audit as create; a missing id ⇒ 404. */
  updateTripType(id: number, input: TripTypeInput) {
    return putJSON<TripTypeMutationResponse>(`/v1/admin/trip-types/${id}`, input);
  },
  /** DELETE /v1/admin/trip-types/:id — delete a trip type. */
  deleteTripType(id: number) {
    return deleteJSON<TripTypeMutationResponse>(`/v1/admin/trip-types/${id}`);
  },
  /** POST /v1/admin/trip-types/:id/status — activate (status:1) or deactivate
   *  (status:0) a trip type without re-posting the whole row. Gated by cms.manage
   *  server-side; audited. */
  setTripTypeStatus(id: number, status: 0 | 1) {
    return postJSON<TripTypeMutationResponse>(`/v1/admin/trip-types/${id}/status`, { status });
  },

  // ── vehicle categories (cms.read; READ-ONLY ride/vehicle-type catalogue) ──
  /** GET /v1/admin/vehicle-categories — the ride/vehicle-type reference catalogue
   *  (all statuses; the staff view). `q` free-text-filters type / description;
   *  `active:1` narrows to status=1. content-svc OWNS the table; this
   *  reads the full catalogue through the shared pool. Defensive: degrades to
   *  available:false when the vehicle_categories table isn't in Postgres yet. */
  vehicleCategories(
    params: { active?: number; q?: string; limit?: number; offset?: number },
    signal?: AbortSignal,
  ) {
    return getJSON<VehicleCategoriesResponse>('/v1/admin/vehicle-categories', params, signal);
  },
  /** GET /v1/admin/vehicle-categories/:id — one category (any status), for the
   *  detail drawer. Defensive: available:false + note when the table is absent. */
  vehicleCategory(id: number, signal?: AbortSignal) {
    return getJSON<{ status: number; available: boolean; category?: VehicleCategory; note?: string }>(
      `/v1/admin/vehicle-categories/${id}`,
      undefined,
      signal,
    );
  },
  /** PUT /v1/admin/vehicle-categories/:id/pricing — edit ONLY the two per-km
   *  surcharges (customer + driver). Gated by fares.manage (finance + superadmin);
   *  admin-svc proxies content-svc (the table owner) + audits the write, and 503s
   *  cleanly until content-svc is wired. base_fare / price_per_km are NOT editable
   *  here (they aren't in fare calc). */
  updateVehicleCategoryPricing(id: number, input: VehicleCategoryPricingInput) {
    return putJSON<VehicleCategoryPricingResponse>(`/v1/admin/vehicle-categories/${id}/pricing`, input);
  },

  // ── complaint categories (cms.read; content-svc owns the tables; READ-ONLY) ──
  /** GET /v1/admin/complaint-categories?active=&q=&limit=&offset= — the complaint
   *  taxonomy list; each row carries its sub-category count. `active:1` narrows to
   *  status=1. content-svc OWNS the tables; this reads the full taxonomy through
   *  the shared pool. Defensive: available:false when complaint_categories isn't
   *  in Postgres yet. */
  complaintCategories(
    params: { active?: number; q?: string; limit?: number; offset?: number },
    signal?: AbortSignal,
  ) {
    return getJSON<ComplaintCategoriesResponse>('/v1/admin/complaint-categories', params, signal);
  },
  /** GET /v1/admin/complaint-categories/:id — one category WITH its nested
   *  sub-categories, for the detail drawer. Defensive: available:false + note when
   *  the table is absent. */
  complaintCategory(id: number, signal?: AbortSignal) {
    return getJSON<{ status: number; available: boolean; category?: ComplaintCategory; note?: string }>(
      `/v1/admin/complaint-categories/${id}`,
      undefined,
      signal,
    );
  },
  // ── complaint authoring (cms.manage — admin-svc owns the CRUD directly +
  //    audits every write; defensive 503 until the tables land) ──
  /** POST /v1/admin/complaint-categories — create a parent category. */
  createComplaintCategory(input: ComplaintCategoryInput) {
    return postJSON<ComplaintCategoryMutationResponse>('/v1/admin/complaint-categories', input);
  },
  /** PUT /v1/admin/complaint-categories/:id — edit a parent category. */
  updateComplaintCategory(id: number, input: ComplaintCategoryInput) {
    return putJSON<ComplaintCategoryMutationResponse>(`/v1/admin/complaint-categories/${id}`, input);
  },
  /** DELETE /v1/admin/complaint-categories/:id — delete a category (its
   *  sub-categories cascade away server-side via the parent FK). */
  deleteComplaintCategory(id: number) {
    return deleteJSON<ComplaintCategoryMutationResponse>(`/v1/admin/complaint-categories/${id}`);
  },
  /** POST /v1/admin/complaint-categories/:id/sub-categories — add a sub-reason
   *  under the parent category :id. */
  createComplaintSubCategory(parentId: number, input: ComplaintSubCategoryInput) {
    return postJSON<ComplaintSubCategoryMutationResponse>(
      `/v1/admin/complaint-categories/${parentId}/sub-categories`,
      input,
    );
  },
  /** PUT /v1/admin/complaint-sub-categories/:id — edit a sub-reason (its parent
   *  is immutable; to re-parent, delete + recreate). */
  updateComplaintSubCategory(id: number, input: ComplaintSubCategoryInput) {
    return putJSON<ComplaintSubCategoryMutationResponse>(`/v1/admin/complaint-sub-categories/${id}`, input);
  },
  /** DELETE /v1/admin/complaint-sub-categories/:id — delete a sub-reason. */
  deleteComplaintSubCategory(id: number) {
    return deleteJSON<ComplaintSubCategoryMutationResponse>(`/v1/admin/complaint-sub-categories/${id}`);
  },

  // ── booking statuses (booking_statuses.read for the list; .manage for the
  //    writes — admin-svc owns the CRUD directly + audits every write) ──
  /** GET /v1/admin/booking-statuses — list every booking-status label (ordered by
   *  id, the status code). Optional free-text q (status / customer message / id).
   *  Defensive: degrades to available:false when the table isn't in Postgres yet. */
  bookingStatuses(params: { q?: string }, signal?: AbortSignal) {
    return getJSON<BookingStatusesResponse>('/v1/admin/booking-statuses', params, signal);
  },
  /** POST /v1/admin/booking-statuses — create a label row. Gated by
   *  booking_statuses.manage server-side; audited. Degrades to a clean 503
   *  (ApiError) when the table isn't in this Postgres yet (pre-cutover). */
  createBookingStatus(input: BookingStatusInput) {
    return postJSON<BookingStatusMutationResponse>('/v1/admin/booking-statuses', input);
  },
  /** PUT /v1/admin/booking-statuses/:id — edit a label row (full overwrite of its
   *  editable fields). A missing id yields a 404 (ApiError). */
  updateBookingStatus(id: number, input: BookingStatusInput) {
    return putJSON<BookingStatusMutationResponse>(`/v1/admin/booking-statuses/${id}`, input);
  },
  /** DELETE /v1/admin/booking-statuses/:id — delete a label row. */
  deleteBookingStatus(id: number) {
    return deleteJSON<BookingStatusMutationResponse>(`/v1/admin/booking-statuses/${id}`);
  },
};
